import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { QrCode, MapPin, Calendar, CreditCard, Clock, Users, Crown, GraduationCap, Car, CheckCircle, XCircle, Menu, X, AlertCircle, Phone, Camera, History, Lock, ArrowLeft, ShieldCheck, KeyRound, Loader, Settings, Save, Mail, Eye, EyeOff, HelpCircle, Edit3, User as UserIcon, Send, Star, MessageCircle, Navigation, Search, Crosshair, ChevronDown, DollarSign, Bell, Wallet, TrendingUp, RefreshCw, Inbox, Package, Truck, ShoppingCart, ChevronRight, Filter, ArrowUpCircle, ArrowDownCircle, RotateCcw, PlusCircle, Info } from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer, Circle } from '@react-google-maps/api';
import authService from './authService';
import BotShield, { botFields, resetBotShield } from './BotShield';
import PhoneVerifyModal from './PhoneVerifyModal'; // V7 : confirmation du numéro par code WhatsApp
import PhoneLoginWeb from './PhoneLoginWeb'; // connexion sans mot de passe « numéro d'abord » (2026-10-30)
import apiService from './apiService';
import TripHistoryClient from './TripHistoryClient';
import { ratingText } from './market/ratingText';
import PaymentModal from './PaymentModal';
import MarketApp from './market/MarketApp';
import LandingPage from './landing/LandingPage';
import IOSInstallBanner from './IOSInstallBanner';

// Composants définis À L'INTÉRIEUR du composant parent (pages, modales) : leur « type » est
// une nouvelle fonction à chaque rendu du parent → React les démonte et les remonte, ce qui
// vide les formulaires en cours de saisie (ex. à chaque poll de 30 s). <Stable render={X} />
// garde un type constant (Stable) tout en exécutant la dernière version de X : l'état interne
// (useState) survit aux rendus du parent, les fermetures restent fraîches.
function Stable({ render, ...props }) { return render(props); }


const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Plan mapping avec noms et couleurs
const PLAN_MAPPING = {
  basic: { name: 'BASIQUE', color: '#10b981', description: 'Trajets fixes partagés' },
  'private-basic': { name: 'PRIVÉ BASIQUE', color: '#FF7800', description: 'Trajets fixes privatisés' },
  standard: { name: 'STANDARD', color: '#FFB900', description: 'Tous trajets partagés' },
  premium: { name: 'PREMIUM', color: '#FF9B00', description: 'Tous trajets privatisés' },
  university: { name: 'UNIVERSITÉ', color: '#06b6d4', description: 'Campus - 05h30 à 19h00' },
  elite: { name: 'ÉLITE', color: '#dc2626', description: 'Taxi privé illimité' }
};

export default function TaxiClientApp() {
  // Lien direct mbiyo.app/b/<supplier_id> : lu dès le premier rendu (avant tout effet de synchronisation d'URL)
  const initialBusinessId = useMemo(() => (window.location.pathname.match(/^\/b\/([A-Za-z0-9_-]+)\/?$/) || [])[1] || null, []);
  const [currentPage, setCurrentPage] = useState(initialBusinessId ? 'business' : 'landing'); // vitrine publique ; l'auth redirige vers home
  const [businessId, setBusinessId] = useState(initialBusinessId);
  // ── Synchronisation avec l'historique du navigateur : le bouton Retour
  //    navigue DANS l'app (pages, et routes internes du Market via state.mk)
  //    au lieu de renvoyer vers le site précédent. ──
  const popNavRef = useRef(false);
  useEffect(() => {
    window.history.replaceState({ page: initialBusinessId ? 'business' : 'landing' }, '');
    const onPop = (e) => {
      const st = e.state || {};
      popNavRef.current = true;
      setCurrentPage(st.page || (localStorage.getItem('userToken') ? 'home' : 'landing'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (popNavRef.current) { popNavRef.current = false; return; }
    if (window.history.state?.page !== currentPage) {
      window.history.pushState({ page: currentPage }, '');
    }
  }, [currentPage]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const currentUserRef = useRef(null);
  const [language, setLanguage] = useState('fr');
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const activeSubscriptionRef = useRef(null);
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [, setLoadingSubscription] = useState(false);
  const [userScanStats, setUserScanStats] = useState({ today: 0, week: 0, month: 0, year: 0, total: 0 });
  const [scanStatsFilter, setScanStatsFilter] = useState('month');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');

  // Mode client : transport | livraison
  const [activeClientMode, setActiveClientMode] = useState(() => localStorage.getItem('clientActiveMode') || 'transport');

  // Solde client
  const [userBalance, setUserBalance] = useState(null);
  const [balanceTransactions, setBalanceTransactions] = useState([]);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Prix des plans depuis l'API
  const [planPrices, setPlanPrices] = useState(null);

  const fetchPlanPrices = async () => {
    try {
      const res = await apiService.get('/subscriptions/plan-prices');
      if (res.success && res.prices) {
        setPlanPrices(res.prices);
      }
    } catch (_) {}
  };

  const fetchUserBalance = async (userId) => {
    try {
      setLoadingBalance(true);
      const res = await apiService.get(`/balance/user/${userId}`);
      if (res.success) {
        setUserBalance(res.balance ?? res);
      }
    } catch (_) {}
    finally { setLoadingBalance(false); }
  };

  // Notifications client
  const [clientNotifications, setClientNotifications] = useState([]);
  const [clientUnreadCount, setClientUnreadCount] = useState(0);
  const [showClientNotifs, setShowClientNotifs] = useState(false);

  // Client notifications - fetching & marking
  const clientNotifsJsonRef = useRef('');
  const loadClientNotifications = useCallback(async () => {
    try {
      const res = await apiService.get('/users/me/notifications?limit=20', { silent401: true });
      if (res.success) {
        const newJson = JSON.stringify(res.notifications || []);
        if (newJson !== clientNotifsJsonRef.current) {
          clientNotifsJsonRef.current = newJson;
          setClientNotifications(res.notifications || []);
        }
        setClientUnreadCount(prev => {
          const next = res.unreadCount || 0;
          return prev === next ? prev : next;
        });
      }
    } catch (err) {
      // silencieux
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadClientNotifications();
      const notifInterval = setInterval(loadClientNotifications, 30000);
      return () => clearInterval(notifInterval);
    }
  }, [isAuthenticated, loadClientNotifications]);

  const markAllClientNotifsRead = async () => {
    try {
      await apiService.patch('/users/me/notifications/mark-all-read');
      setClientUnreadCount(0);
      setClientNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      // silencieux
    }
  };
  
  // Use ref to track which trips have been shown (avoids re-render and dependency issues)

  
  // Récupérer l'abonnement actif de l'utilisateur
  const fetchUserSubscription = async (userId) => {
    try {
      setLoadingSubscription(true);
      const response = await apiService.get(`/subscriptions/user/${userId}`);
      if (response.success && response.subscription) {
        setActiveSubscription(response.subscription);
      } else {
        setActiveSubscription(null);
      }
    } catch (error) {
      console.error('Erreur récupération abonnement:', error);
      setActiveSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Récupérer les statistiques de scans du client
  const fetchUserScanStats = async (userId) => {
    try {
      const data = await apiService.get(`/scans/user/${userId}/stats`, { userType: 'user', silent401: true });
      if (data.success && data.stats) {
        setUserScanStats(data.stats);
      }
    } catch (error) {
      console.error('Erreur récupération stats scans:', error);
    }
  };
  
  // Connexion par téléphone (2026-10-30) : session de 180 jours → reprise sans code à l'ouverture
  React.useEffect(() => {
    if (authService.isAuthenticated() || !authService.hasRefresh()) return;
    const publicPath = /^\/(b\/|marche|support)/.test(window.location.pathname);
    authService.restoreSession().then((r) => {
      if (!r?.success) return;
      setCurrentUser(r.user);
      setIsAuthenticated(true);
      if (!publicPath) setCurrentPage('home');
      if (r.user?.user_id) fetchUserSubscription(r.user.user_id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Vérifier si l'utilisateur est déjà connecté au chargement
  React.useEffect(() => {
    // Lien direct vers un commerce : mbiyo.app/b/<supplier_id> — page publique (connecté ou non)
    const bizMatch = window.location.pathname.match(/^\/b\/([A-Za-z0-9_-]+)\/?$/);
    if (bizMatch) {
      setBusinessId(bizMatch[1]);
      setCurrentPage('business');
      fetchPlanPrices();
      if (authService.isAuthenticated()) {
        const u = authService.getCurrentUser();
        setCurrentUser(u);
        setIsAuthenticated(true);
      }
      return;
    }
    // Marché public : mbiyo.app/marche — boutiques et prix consultables sans compte (banque, 2026-10-24)
    if (window.location.pathname === '/marche' || window.location.pathname === '/marche/') {
      setCurrentPage('market');
      fetchPlanPrices();
      if (authService.isAuthenticated()) {
        setCurrentUser(authService.getCurrentUser());
        setIsAuthenticated(true);
      }
      return;
    }
    // Connexion demandée depuis le marché public (?connexion=1)
    if (new URLSearchParams(window.location.search).get('connexion') === '1' && !authService.isAuthenticated()) {
      window.history.replaceState(null, '', '/');
      setCurrentPage('login');
      fetchPlanPrices();
      return;
    }
    // Détecter l'URL /support pour accès direct sans connexion
    if (window.location.pathname === '/support') {
      setCurrentPage('support');
      fetchPlanPrices();
      if (authService.isAuthenticated()) {
        const u = authService.getCurrentUser();
        setCurrentUser(u);
        setIsAuthenticated(true);
      }
      return;
    }
    if (authService.isAuthenticated()) {
      const user = authService.getCurrentUser();
      setCurrentUser(user);
      setIsAuthenticated(true);
      setCurrentPage('home');
      if (user?.user_id) {
        fetchUserSubscription(user.user_id);
        fetchUserScanStats(user.user_id);
        fetchUserBalance(user.user_id);
      }
    }
    fetchPlanPrices();
  }, []);

  // Sync URL avec currentPage
  React.useEffect(() => {
    if (currentPage === 'support') {
      if (window.location.pathname !== '/support') window.history.pushState(null, '', '/support');
    } else if (currentPage === 'market') {
      if (window.location.pathname !== '/marche') window.history.replaceState(window.history.state, '', '/marche');
    } else if (currentPage === 'business') {
      // Garder /b/<id> dans l'adresse (rechargement, partage) sans créer d'entrée d'historique
      if (businessId && window.location.pathname !== `/b/${businessId}`) window.history.replaceState(window.history.state, '', `/b/${businessId}`);
    } else {
      if (window.location.pathname === '/support' || window.location.pathname.startsWith('/b/') || window.location.pathname === '/marche') window.history.pushState(null, '', '/');
    }
  }, [currentPage]);

  // Sync refs pour éviter les stale closures dans les composants memoizés
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
  useEffect(() => { activeSubscriptionRef.current = activeSubscription; }, [activeSubscription]);

  // Recharger les stats de scans automatiquement toutes les 60 secondes (polling) — uniquement sur la page d'accueil
  React.useEffect(() => {
    if (!isAuthenticated || !currentUser?.user_id || currentPage !== 'home') return;
    
    const interval = setInterval(() => {
      fetchUserScanStats(currentUser.user_id);
    }, 60000); // 60 secondes

    return () => clearInterval(interval);
  }, [isAuthenticated, currentUser?.user_id, currentPage]);


  const translations = {
    fr: {
      home: 'Accueil',
      myQR: 'Mon QR Code',
      subscription: 'Abonnement',
      routes: 'Trajets',
      history: 'Historique',
      settings: 'Paramètres',
      welcome: 'Bienvenue',
      activeUntil: 'Actif jusqu\'au',
      daysLeft: 'jours restants',
      scanToRide: 'Scannez pour voyager',
      showDriver: 'Montrez ce code au chauffeur',
      renewNow: 'Renouveler maintenant',
      subscriptionExpired: 'Abonnement expiré',
      selectPlan: 'Choisissez votre formule',
      monthly: 'Mensuel',
      yearly: 'Annuel',
      perMonth: '/mois',
      perYear: '/an',
      save: 'Économisez',
      basicPlan: 'BASIQUE',
      basicDesc: 'Trajets fixes partagés',
      privateBasicPlan: 'PRIVÉ BASIQUE',
      privateBasicDesc: 'Trajets fixes privatisés',
      standardPlan: 'STANDARD',
      standardDesc: 'Tous trajets partagés',
      premiumPlan: 'PREMIUM',
      premiumDesc: 'Tous trajets privatisés',
      universityPlan: 'UNIVERSITÉ',
      universityDesc: 'Campus - 05h30 à 19h00',
      elitePlan: 'ÉLITE',
      eliteDesc: 'Taxi privé illimité',
      subscribe: 'S\'abonner',
      loginRequired: 'Veuillez vous connecter d\'abord',
      paymentMethod: 'Mode de paiement',
      availableRoutes: 'Trajets disponibles',
      allRoutes: 'Tous les trajets',
      limitedRoutes: 'Trajets limités',
      shared: 'Partagé',
      private: 'Privatisé',
      unlimited: 'Illimité',
      timeRestriction: 'Horaires',
      // Features de plans
      limitedTrips: 'Trajets limités',
      economical: 'Économique',
      comfortable: 'Confortable',
      allTrips: 'Tous les trajets',
      maxFreedom: 'Liberté maximale',
      guaranteedPrivacy: 'Intimité garantie',
      campusOnly: 'Campus uniquement',
      fixedSchedule: 'Horaires fixes',
      studentRate: 'Tarif étudiant',
      vipPreference: 'Préférence VIP',
      unlimited7to7: '05:30 - 19:00'
    },
    en: {
      home: 'Home',
      myQR: 'My QR Code',
      subscription: 'Subscription',
      routes: 'Routes',
      history: 'History',
      settings: 'Settings',
      welcome: 'Welcome',
      activeUntil: 'Active until',
      daysLeft: 'days left',
      scanToRide: 'Scan to ride',
      showDriver: 'Show this code to driver',
      renewNow: 'Renew now',
      subscriptionExpired: 'Subscription expired',
      selectPlan: 'Select your plan',
      monthly: 'Monthly',
      yearly: 'Yearly',
      perMonth: '/month',
      perYear: '/year',
      save: 'Save',
      basicPlan: 'BASIC',
      basicDesc: 'Fixed shared routes',
      privateBasicPlan: 'PRIVATE BASIC',
      privateBasicDesc: 'Fixed private routes',
      standardPlan: 'STANDARD',
      standardDesc: 'All shared routes',
      premiumPlan: 'PREMIUM',
      premiumDesc: 'All private routes',
      universityPlan: 'UNIVERSITY',
      universityDesc: 'Campus - 5:30 AM to 7:00 PM',
      elitePlan: 'ELITE',
      eliteDesc: 'Unlimited private taxi',
      subscribe: 'Subscribe',
      loginRequired: 'Please log in first',
      paymentMethod: 'Payment method',
      availableRoutes: 'Available routes',
      allRoutes: 'All routes',
      limitedRoutes: 'Limited routes',
      shared: 'Shared',
      private: 'Private',
      unlimited: 'Unlimited',
      timeRestriction: 'Schedule',
      // Features de plans
      limitedTrips: 'Limited trips',
      economical: 'Economical',
      comfortable: 'Comfortable',
      allTrips: 'All trips',
      maxFreedom: 'Maximum freedom',
      guaranteedPrivacy: 'Guaranteed privacy',
      campusOnly: 'Campus only',
      fixedSchedule: 'Fixed schedule',
      studentRate: 'Student rate',
      vipPreference: 'VIP preference',
      unlimited7to7: '7:00 AM - 7:00 PM'
    },
    sw: {
      home: 'Nyumbani',
      myQR: 'QR Yangu',
      subscription: 'Usajili',
      routes: 'Njia',
      history: 'Historia',
      settings: 'Mipangilio',
      welcome: 'Karibu',
      activeUntil: 'Inaendelea hadi',
      daysLeft: 'siku zilizobaki',
      scanToRide: 'Scan kusafiri',
      showDriver: 'Onyesha dereva code hii',
      renewNow: 'Piga upya sasa',
      subscriptionExpired: 'Usajili umeisha',
      selectPlan: 'Chagua mpango wako',
      monthly: 'Kila mwezi',
      yearly: 'Kila mwaka',
      perMonth: '/mwezi',
      perYear: '/mwaka',
      save: 'Okoa',
      basicPlan: 'MSINGI',
      basicDesc: 'Njia zilizowekwa - pamoja',
      privateBasicPlan: 'BINAFSI MSINGI',
      privateBasicDesc: 'Njia zilizowekwa - binafsi',
      standardPlan: 'KAWAIDA',
      standardDesc: 'Njia zote - pamoja',
      premiumPlan: 'PREMIUM',
      premiumDesc: 'Njia zote - binafsi',
      universityPlan: 'CHUO KIKUU',
      universityDesc: 'Campus - 05:30 hadi 19:00',
      elitePlan: 'ELITE',
      eliteDesc: 'Teksi binafsi bila kikomo',
      subscribe: 'Jisajili',
      loginRequired: 'Tafadhali ingia kwanza',
      paymentMethod: 'Njia ya malipo',
      availableRoutes: 'Njia zinazopatikana',
      allRoutes: 'Njia zote',
      limitedRoutes: 'Njia chache',
      shared: 'Pamoja',
      private: 'Binafsi',
      unlimited: 'Bila kikomo',
      timeRestriction: 'Ratiba',
      // Features de plans
      limitedTrips: 'Safari chache',
      economical: 'Nafuu',
      comfortable: 'Starehe',
      allTrips: 'Safari zote',
      maxFreedom: 'Uhuru kamili',
      guaranteedPrivacy: 'Faragha iliyotiwa dhamana',
      campusOnly: 'Campus tu',
      fixedSchedule: 'Ratiba iliyowekwa',
      studentRate: 'Bei ya mwanafunzi',
      vipPreference: 'Pendekezo la VIP',
      unlimited7to7: '05:30 - 19:00'
    },
    ln: {
      home: 'Ndako',
      myQR: 'QR na ngai',
      subscription: 'Abonnement',
      routes: 'Banzela',
      history: 'Makambo ya kala',
      settings: 'Ba paramètres',
      welcome: 'Mbote',
      activeUntil: 'Ezali kosala tii na',
      daysLeft: 'mikolo etikali',
      scanToRide: 'Scan mpo na kokende',
      showDriver: 'Lakisa code oyo na moto ya motuka',
      renewNow: 'Zongisa sika',
      subscriptionExpired: 'Abonnement esilaki',
      selectPlan: 'Pona formule na yo',
      monthly: 'Sanza na sanza',
      yearly: 'Mbula na mbula',
      perMonth: '/sanza',
      perYear: '/mbula',
      save: 'Bomba',
      basicPlan: 'YA MOBOKO',
      basicDesc: 'Banzela ya kokabwana',
      privateBasicPlan: 'BINAFSI YA MOBOKO',
      privateBasicDesc: 'Banzela ya yo moko',
      standardPlan: 'STANDARD',
      standardDesc: 'Banzela nyonso ya kokabwana',
      premiumPlan: 'PREMIUM',
      premiumDesc: 'Banzela nyonso ya yo moko',
      universityPlan: 'UNIVERSITÉ',
      universityDesc: 'Campus - 05h30 na 19h00',
      elitePlan: 'ÉLITE',
      eliteDesc: 'Motuka ya yo moko ntango nyonso',
      subscribe: 'Komipesa',
      paymentMethod: 'Lolenge ya kofuta',
      availableRoutes: 'Banzela oyo ezali',
      allRoutes: 'Banzela nyonso',
      limitedRoutes: 'Banzela moke',
      shared: 'Ya kokabwana',
      private: 'Ya yo moko',
      unlimited: 'Ezanga ndelo',
      timeRestriction: 'Ba ngonga',
      // Features de plans
      limitedTrips: 'Banzela moke',
      economical: 'Marché',
      comfortable: 'Starehe',
      allTrips: 'Banzela nyonso',
      maxFreedom: 'Liberté nyonso',
      guaranteedPrivacy: 'Sekele ya yo moko',
      campusOnly: 'Campus tu',
      fixedSchedule: 'Ngonga iliyowekwa',
      studentRate: 'Prestige ya mwanafunzi',
      vipPreference: 'Preference ya VIP',
      unlimited7to7: '05:30 - 19:00'
    }
  };

  const t = translations[language];

  // Déterminer les données d'affichage basées sur l'abonnement
  const getPlanDisplayName = (planType) => {
    if (!planType) return 'Aucun abonnement';
    return PLAN_MAPPING[planType]?.name || planType.toUpperCase();
  };

  const getPlanColor = (planType) => {
    if (!planType) return '#999';
    return PLAN_MAPPING[planType]?.color || '#666';
  };

  const getDaysLeft = () => {
    if (!activeSubscription) return 0;
    const now = new Date();
    const expiry = new Date(activeSubscription.valid_until);
    const daysLeft = Math.max(0, Math.floor((expiry - now) / (1000 * 60 * 60 * 24)));
    return daysLeft;
  };

  // Simulation de données utilisateur (avec fallback)
  const user = {
    name: currentUser?.name || 'Utilisateur',
    plan: activeSubscription ? getPlanDisplayName(activeSubscription.plan_type) : 'STANDARD',
    planType: activeSubscription?.plan_type || 'standard',
    expiryDate: activeSubscription ? new Date(activeSubscription.valid_until).toLocaleDateString('fr-FR') : '2026-02-24',
    qrCodeImage: activeSubscription?.qr_code || null,  // Image Base64 depuis la base de données
    qrCode: activeSubscription ? `${activeSubscription.subscription_id}|${currentUser?.user_id}` : 'STD-2024-NO-SUB',  // Valeur du code QR
    daysLeft: getDaysLeft(),
    planColor: getPlanColor(activeSubscription?.plan_type),
    photo: currentUser?.photo || null  // Photo de profil en base64
  };

  const subscriptionPlans = [
    {
      id: 'basic',
      name: t.basicPlan,
      description: t.basicDesc,
      monthlyPrice: '45000',
      yearlyPrice: '486000',
      color: '#10b981',
      icon: MapPin,
      features: [t.limitedTrips, t.shared, t.economical]
    },
    {
      id: 'private-basic',
      name: t.privateBasicPlan,
      description: t.privateBasicDesc,
      monthlyPrice: '100000',
      yearlyPrice: '1080000',
      color: '#FF7800',
      icon: Car,
      features: [t.limitedTrips, t.private, t.comfortable]
    },
    {
      id: 'standard',
      name: t.standardPlan,
      description: t.standardDesc,
      monthlyPrice: '60000',
      yearlyPrice: '648000',
      color: '#FFB900',
      icon: Users,
      features: [t.allTrips, t.shared, t.maxFreedom]
    },
    {
      id: 'premium',
      name: t.premiumPlan,
      description: t.premiumDesc,
      monthlyPrice: '150000',
      yearlyPrice: '1620000',
      color: '#FF9B00',
      icon: Crown,
      features: [t.allTrips, t.private, t.guaranteedPrivacy]
    },
    {
      id: 'university',
      name: t.universityPlan,
      description: t.universityDesc,
      monthlyPrice: '55000',
      yearlyPrice: '594000',
      color: '#06b6d4',
      icon: GraduationCap,
      features: [t.campusOnly, t.fixedSchedule, t.studentRate]
    },
    {
      id: 'elite',
      name: t.elitePlan,
      description: t.eliteDesc,
      monthlyPrice: '2000000',
      yearlyPrice: '21600000',
      color: '#dc2626',
      icon: Crown,
      features: [t.unlimited, '07:00 - 19:00', t.vipPreference]
    }
  ];

  // Charger les routes depuis l'API
  const loadRoutes = async () => {
    try {
      setLoadingRoutes(true);
      const data = await apiService.request('/routes');
      if (data.success && data.routes) {
        setRoutes(data.routes);
      }
    } catch (error) {
      console.error('Erreur chargement routes:', error);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // Charger les routes quand la page routes est affichée
  React.useEffect(() => {
    if (currentPage === 'routes' && routes.length === 0) {
      loadRoutes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Page de connexion
  // Connexion sans mot de passe « numéro d'abord » (2026-10-30) — même parcours que l'app :
  // numéro → code WhatsApp → compte connu = session ; nouveau = nom, e-mail facultatif, ville → session.
  // Plus de page d'inscription ni de mot de passe sur le site ; session de 180 jours sur ce navigateur.
  const LoginPage = () => {
    const enterApp = (user) => {
      setCurrentUser(user);
      setIsAuthenticated(true);
      setCurrentPage('home');
      if (user?.user_id) {
        fetchUserSubscription(user.user_id);
      }
    };
    const onSession = async (resp) => {
      const persisted = await authService.persistOtpSession(resp);
      if (!persisted?.success) throw persisted;
      enterApp(persisted.user);
    };

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <PhoneLoginWeb
              app="client"
              accent="#F54119"
              brand={<h1 style={{ textAlign: 'left', fontSize: '1.6rem', marginBottom: '0.25rem' }}>Mbiyo</h1>}
              onSession={onSession}
            />

            <div style={{ borderTop: '1px solid #E9EBEF', marginTop: '1.25rem', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', alignItems: 'center' }}>
              <a
                href="https://chauffeur.taxicenterapp.com"
                style={{ color: '#C9350F', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                🚗 Devenir chauffeur
              </a>
              <a
                href="https://mbiyo.app/telecharger"
                style={{ color: '#059669', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                🛒 Vendre sur Mbiyo — application Mbiyo Business
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ForgotPasswordPage = () => {
    const [step, setStep] = useState(1); // 1: Saisir téléphone, 2: Saisir code, 3: Nouveau mot de passe
    const [phone, setPhone] = useState('+243 ');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailHint, setEmailHint] = useState('');
    const [devCode, setDevCode] = useState('');
    const [countdown, setCountdown] = useState(0);
    const [channel, setChannel] = useState('email'); // V7 : 'whatsapp' | 'email'

    // Countdown pour renvoyer le code
    React.useEffect(() => {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timer);
      }
    }, [countdown]);

    const handlePhoneChange = (e) => {
      let value = e.target.value;
      if (!value.startsWith('+243')) {
        value = '+243 ';
      }
      value = value.replace(/[^\d+\s]/g, '');
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length > 12) return;
      setPhone(value);
    };

    // Étape 1 : Demander le code
    const handleRequestCode = async (e) => {
      e.preventDefault();
      const cleanPhone = phone.replace(/\s/g, '');
      
      if (cleanPhone.length < 13) {
        setError('Numéro de téléphone incomplet');
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        // V7 : WhatsApp d'abord, e-mail en secours
        const result = await authService.forgotPassword(cleanPhone, { channel: 'auto', language: 'fr' });
        if (result.success) {
          setChannel(result.channel || 'email');
          setEmailHint(result.hint || result.emailHint || '');
          if (result.resetCode) {
            setDevCode(result.resetCode); // Mode dev uniquement
          }
          setStep(2);
          setCountdown(60); // 60 secondes avant de pouvoir renvoyer
          setSuccess(result.channel === 'whatsapp' ? 'Un code a été envoyé sur WhatsApp.' : 'Un code de vérification a été envoyé par e-mail.');
        } else {
          setError(result.error || 'Erreur lors de l\'envoi du code');
        }
      } catch (err) {
        setError('Erreur: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    // Renvoyer le code (V7 : wantedChannel 'email' → bascule vers l'e-mail à la demande)
    const handleResendCode = async (wantedChannel) => {
      if (countdown > 0 && wantedChannel !== 'email') return;
      setIsLoading(true);
      setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authService.forgotPassword(cleanPhone, { channel: wantedChannel === 'email' ? 'email' : 'auto', language: 'fr' });
        if (result.success) {
          setChannel(result.channel || 'email');
          setEmailHint(result.hint || result.emailHint || '');
          if (result.resetCode) setDevCode(result.resetCode);
          setCountdown(60);
          setSuccess(result.channel === 'whatsapp' ? 'Un nouveau code a été envoyé sur WhatsApp.' : 'Un nouveau code a été envoyé par e-mail.');
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Erreur: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    // Étape 2 : Vérifier le code
    const handleVerifyCode = async (e) => {
      e.preventDefault();
      if (resetCode.length !== 6) {
        setError('Le code doit contenir 6 chiffres');
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authService.verifyResetCode(cleanPhone, resetCode);
        if (result.success) {
          setStep(3);
          setSuccess('Code vérifié ! Choisissez votre nouveau mot de passe.');
        } else {
          setError(result.error || 'Code incorrect');
        }
      } catch (err) {
        setError('Erreur: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    // Étape 3 : Réinitialiser le mot de passe
    const handleResetPassword = async (e) => {
      e.preventDefault();
      
      if (newPassword.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const cleanPhone = phone.replace(/\s/g, '');
        const result = await authService.resetPassword(cleanPhone, resetCode, newPassword);
        if (result.success) {
          setSuccess('✅ Mot de passe réinitialisé avec succès !');
          setTimeout(() => {
            setCurrentPage('login');
          }, 2000);
        } else {
          setError(result.error || 'Erreur lors de la réinitialisation');
        }
      } catch (err) {
        setError('Erreur: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <button 
                onClick={() => step === 1 ? setCurrentPage('login') : setStep(step - 1)}
                style={{ 
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem',
                  display: 'flex', alignItems: 'center', color: '#F54119'
                }}
              >
                <ArrowLeft size={22} />
              </button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontSize: '1.3rem' }}>🔒 Réinitialisation</h1>
              </div>
              <div style={{ width: '34px' }}></div>
            </div>

            {/* Indicateur d'étapes */}
            <div style={{ 
              display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.5rem' 
            }}>
              {[1, 2, 3].map(s => (
                <div key={s} style={{
                  width: s === step ? '2rem' : '0.75rem',
                  height: '0.75rem',
                  borderRadius: '0.375rem',
                  background: s <= step ? '#F54119' : '#E9EBEF',
                  transition: 'all 0.3s ease'
                }} />
              ))}
            </div>

            {error && (
              <div className="error-message">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            {success && !error && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.75rem 1rem', background: '#ecfdf5', border: '1px solid #a7f3d0',
                borderRadius: '8px', marginBottom: '1rem', color: '#065f46', fontSize: '0.9rem'
              }}>
                <CheckCircle size={18} />
                {success}
              </div>
            )}

            {/* ÉTAPE 1 : Saisir le numéro de téléphone */}
            {step === 1 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
                  }}>
                    <KeyRound size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>
                    Entrez votre numéro de téléphone. Un code de réinitialisation sera envoyé sur votre WhatsApp (ou par e-mail si vous n'avez pas WhatsApp).
                  </p>
                </div>

                <form onSubmit={handleRequestCode}>
                  <div className="form-group">
                    <label>Numéro de téléphone</label>
                    <div className="phone-input-wrapper">
                      <Phone size={20} className="phone-icon" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={handlePhoneChange}
                        placeholder="+243 XXX XXX XXX"
                        required
                      />
                    </div>
                    <small className="input-hint">Format: +243 suivi de 9 chiffres</small>
                  </div>

                  <button type="submit" className="btn-auth" disabled={isLoading}>
                    {isLoading ? '⏳ Envoi en cours...' : '📨 Envoyer le code'}
                  </button>
                </form>
              </>
            )}

            {/* ÉTAPE 2 : Saisir le code de vérification */}
            {step === 2 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
                  }}>
                    <ShieldCheck size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>
                    {channel === 'whatsapp' ? 'Entrez le code à 6 chiffres envoyé sur WhatsApp au' : 'Entrez le code à 6 chiffres envoyé à votre adresse email'}
                    {emailHint && <><br/><strong style={{ color: '#F54119' }}>{emailHint}</strong></>}
                  </p>
                </div>

                {/* Mode dev : afficher le code */}
                {devCode && (
                  <div style={{
                    padding: '0.75rem', background: '#fef3c7', border: '1px solid #fcd34d',
                    borderRadius: '8px', marginBottom: '1rem', textAlign: 'center'
                  }}>
                    <small style={{ color: '#92400e' }}>🧪 Mode dev — Code: <strong style={{ fontSize: '1.1rem', letterSpacing: '2px' }}>{devCode}</strong></small>
                  </div>
                )}

                <form onSubmit={handleVerifyCode}>
                  <div className="form-group">
                    <label>Code de vérification</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={resetCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        if (val.length <= 6) setResetCode(val);
                      }}
                      placeholder="• • • • • •"
                      style={{ 
                        textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem',
                        fontWeight: '600'
                      }}
                      required
                    />
                  </div>

                  <button type="submit" className="btn-auth" disabled={isLoading || resetCode.length !== 6}>
                    {isLoading ? '⏳ Vérification...' : '✅ Vérifier le code'}
                  </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  {countdown > 0 ? (
                    <p style={{ color: '#9AA0A8', fontSize: '0.85rem' }}>
                      Renvoyer le code dans {countdown}s
                    </p>
                  ) : (
                    <span
                      onClick={() => handleResendCode()}
                      style={{ color: '#F54119', cursor: 'pointer', fontSize: '0.9rem' }}
                    >
                      🔄 Renvoyer le code
                    </span>
                  )}
                  {channel === 'whatsapp' && (
                    <p style={{ marginTop: '0.6rem' }}>
                      <span onClick={() => handleResendCode('email')} style={{ color: '#5C636E', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}>
                        Je n'ai pas WhatsApp — recevoir le code par e-mail
                      </span>
                    </p>
                  )}
                </div>
              </>
            )}

            {/* ÉTAPE 3 : Nouveau mot de passe */}
            {step === 3 && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '60px', height: '60px', background: '#FEE9E2', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
                  }}>
                    <Lock size={28} color="#F54119" />
                  </div>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', margin: 0 }}>
                    Choisissez votre nouveau mot de passe (minimum 8 caractères)
                  </p>
                </div>

                <form onSubmit={handleResetPassword}>
                  <div className="form-group">
                    <label>Nouveau mot de passe</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={8}
                      required
                    />
                    {newPassword.length > 0 && newPassword.length < 8 && (
                      <small style={{ color: '#DC2626' }}>
                        Minimum 8 caractères ({8 - newPassword.length} restants)
                      </small>
                    )}
                    {newPassword.length >= 8 && (
                      <small style={{ color: '#10b981' }}>✅ Longueur OK</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Confirmer le mot de passe</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                    {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                      <small style={{ color: '#DC2626' }}>Les mots de passe ne correspondent pas</small>
                    )}
                    {confirmPassword.length > 0 && newPassword === confirmPassword && (
                      <small style={{ color: '#10b981' }}>✅ Les mots de passe correspondent</small>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="btn-auth" 
                    disabled={isLoading || newPassword.length < 8 || newPassword !== confirmPassword}
                  >
                    {isLoading ? '⏳ Réinitialisation...' : '🔐 Réinitialiser le mot de passe'}
                  </button>
                </form>
              </>
            )}

            <p className="auth-switch" style={{ marginTop: '1.5rem' }}>
              <span onClick={() => setCurrentPage('login')} style={{ color: '#F54119', cursor: 'pointer' }}>
                ← Retour à la connexion
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Page d'inscription
  // Page d'inscription avec photo obligatoire et email facultatif
  const CameraModal = ({ onCapture, onClose }) => {
    const videoRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraError, setCameraError] = useState('');
    const streamRef = React.useRef(null);

    React.useEffect(() => {
      let timeoutId = null;

      const initCamera = async () => {
        try {
          setCameraError('');
          console.log('🎥 Demande d\'accès à la caméra...');
          
          // Vérifier que mediaDevices est disponible
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('mediaDevices_not_available');
          }
          
          // Demande explicite d'accès à la caméra
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: 'user',
              width: { ideal: 640 },
              height: { ideal: 480 }
            },
            audio: false
          });
          
          console.log('✅ Caméra obtenue:', stream.getVideoTracks());
          streamRef.current = stream;
          
          if (!videoRef.current) return;
          
          // Assigner le stream
          videoRef.current.srcObject = stream;
          console.log('📹 Stream assigné à la vidéo');
          
          // Appeler play() et attendre que ça marche
          try {
            await videoRef.current.play();
            console.log('▶️ Video play() exécuté avec succès');
          } catch (err) {
            console.warn('⚠️ play() erreur (normal pour streaming):', err.message);
          }
          
          // Timeout simple pour l'activation
          timeoutId = setTimeout(() => {
            console.log('⏱️ Activation timeout');
            setCameraActive(true);
          }, 500);
          
        } catch (err) {
          console.error('❌ Erreur caméra:', err);
          let errorMsg = '⚠️ Impossible d\'accéder à la caméra. ';
          
          if (err.name === 'NotAllowedError' || err.message === 'Permission denied') {
            errorMsg = '❌ Permission refusée. Autorise l\'accès à la caméra dans les paramètres du navigateur.';
          } else if (err.name === 'NotFoundError') {
            errorMsg = '❌ Aucune caméra trouvée. Vérifie que tu as une caméra connectée.';
          } else if (err.name === 'NotReadableError') {
            errorMsg = '❌ La caméra est déjà utilisée par une autre application.';
          } else if (err.name === 'TypeError' || err.message === 'mediaDevices_not_available') {
            errorMsg = '❌ Accès caméra non disponible. Cette fonction nécessite HTTPS ou localhost.';
          } else if (err.message && err.message.includes('Permission')) {
            errorMsg = '❌ Permission de caméra requise. Clique sur l\'icône de caméra dans la barre du navigateur.';
          } else {
            errorMsg += (err.message || err.name || 'Erreur inconnue');
          }
          setCameraError(errorMsg);
        }
      };
      
      initCamera();
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (streamRef.current) {
          console.log('🛑 Arrêt des tracks');
          streamRef.current.getTracks().forEach(track => {
            track.stop();
          });
          streamRef.current = null;
        }
      };
    }, []);

    const capturePhoto = () => {
      if (!videoRef.current || !canvasRef.current) {
        console.error('Canvas ou vidéo non disponible');
        return;
      }

      try {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        console.log(`📸 Capture: ${canvas.width}x${canvas.height}`);
        ctx.drawImage(video, 0, 0);

        const imgData = canvas.toDataURL('image/jpeg');
        const img = new Image();
        img.src = imgData;

        img.onload = () => {
          const maxWidth = 400;
          const maxHeight = 400;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round(height * maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round(width * maxHeight / height);
              height = maxHeight;
            }
          }

          const finalCanvas = document.createElement('canvas');
          finalCanvas.width = width;
          finalCanvas.height = height;
          const finalCtx = finalCanvas.getContext('2d');
          finalCtx.drawImage(img, 0, 0, width, height);

          const compressedPhoto = finalCanvas.toDataURL('image/jpeg', 0.8);
          
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          
          console.log('✅ Photo capturée');
          onCapture(compressedPhoto);
        };
      } catch (err) {
        console.error('❌ Erreur capture:', err);
        setCameraError('Erreur capture: ' + err.message);
      }
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <h3>Prendre une photo</h3>
            <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer'}}>✕</button>
          </div>

          {cameraError && (
            <div style={{
              padding: '1rem',
              background: '#fee2e2',
              border: '1px solid #FEE2E2',
              borderRadius: '8px',
              color: '#991b1b',
              marginBottom: '1rem'
            }}>
              <p style={{margin: 0}}>⚠️ {cameraError}</p>
            </div>
          )}

          <div style={{
            position: 'relative',
            background: '#000',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '1rem',
            minHeight: '320px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            {!cameraActive && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                textAlign: 'center'
              }}>
                <p style={{fontSize: '1.1rem'}}>🎥 Initialisation de la caméra...</p>
              </div>
            )}
          </div>

          <canvas ref={canvasRef} style={{display: 'none'}} />

          <div style={{display: 'flex', gap: '1rem'}}>
            <button
              onClick={capturePhoto}
              disabled={!cameraActive}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                background: cameraActive ? '#FF7800' : '#DCE0E6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: cameraActive ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              📸 Capturer
            </button>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                background: '#E9EBEF',
                color: '#333',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem'
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RegisterPage = () => {
    const [formData, setFormData] = useState({
      name: '',
      phone: '+243 ',
      email: '',
      dateOfBirth: '',
      password: '',
      confirmPassword: ''
    });
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [error, setError] = useState('');
    const [showCameraModal, setShowCameraModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [otp, setOtp] = useState(null); // V7 : { phone, email, name, bot } → code WhatsApp avant la création
    const fileInputRef = React.useRef(null);

    const handlePhoneChange = (e) => {
      let value = e.target.value;
      
      if (!value.startsWith('+243')) {
        value = '+243 ';
      }
      
      value = value.replace(/[^\d+\s]/g, '');
      
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length > 12) return;
      
      setFormData({ ...formData, phone: value });
    };

    const compressPhoto = (imgSrc, callback) => {
      const img = new Image();
      img.src = imgSrc;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 400;
        const maxHeight = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * maxWidth / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * maxHeight / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const compressedPhoto = canvas.toDataURL('image/jpeg', 0.8);
        callback(compressedPhoto);
      };
    };

    const handlePhotoCaptured = (photoData) => {
      setPhoto(photoData);
      setPhotoPreview(photoData);
      setError('');
      setShowCameraModal(false);
    };

    const handlePhotoFromFile = (e) => {
      const file = e.target.files[0];
      if (file) {
        // Vérifier que c'est une image
        if (!file.type.startsWith('image/')) {
          setError('Veuillez sélectionner une image');
          return;
        }

        // Vérifier la taille (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setError('La photo est trop volumineuse (max 5MB)');
          return;
        }

        // Convertir en base64 et compresser l'image
        const reader = new FileReader();
        reader.onloadend = () => {
          compressPhoto(reader.result, (compressed) => {
            setPhoto(compressed);
            setPhotoPreview(compressed);
            setError('');
          });
        };
        reader.readAsDataURL(file);
      }
    };

    const handleRegister = async (e) => {
      e.preventDefault();
      
      const cleanPhone = formData.phone.replace(/\s/g, '');
      
      if (cleanPhone.length < 13) {
        setError('Numéro de téléphone incomplet');
        return;
      }

      if (!photo) {
        setError('La photo de profil est obligatoire');
        return;
      }
      
      if (formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }

      if (formData.password.length < 8) {
        setError('Le mot de passe doit contenir au moins 8 caractères');
        return;
      }

      // Validation email si fourni
      if (formData.email && !formData.email.includes('@')) {
        setError('Email invalide');
        return;
      }

      // V7 : le numéro est confirmé par un code WhatsApp AVANT la création du compte
      // (les champs BotShield partent avec la demande de code ; le jeton de vérification dispense
      // ensuite l'inscription du captcha, à usage unique)
      setError('');
      setOtp({ phone: cleanPhone, email: formData.email || undefined, name: formData.name, bot: botFields('client-register') });
    };
    const submitRegistration = async (verificationToken) => {
      setOtp(null);
      const cleanPhone = formData.phone.replace(/\s/g, '');
      setIsSubmitting(true);
      try {
        const result = await authService.register({
          name: formData.name,
          phone: cleanPhone,
          email: formData.email || null,
          dateOfBirth: formData.dateOfBirth || null,
          password: formData.password,
          photo: photo,
          role: 'client',
          phone_verification_token: verificationToken,
          bot: botFields('client-register'),
        });
        resetBotShield('client-register');

        if (result.success) {
          setCurrentUser(result.user);
          setIsAuthenticated(true);
          setCurrentPage('home');
          // Charger l'abonnement actif de l'utilisateur après inscription
          if (result.user?.user_id) {
            fetchUserSubscription(result.user.user_id);
          }
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError('Erreur lors de l\'inscription: ' + err.message);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleChange = (e) => {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    };

    return (
      <>
        <div className="auth-page">
          <div className="auth-container">
            <div className="auth-card">
              <h1>Mbiyo</h1>
              <h2>Créer un compte</h2>
              <p className="auth-subtitle">Rejoignez Mbiyo aujourd'hui</p>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

            <PhoneVerifyModal open={!!otp} phone={otp?.phone} purpose="register" email={otp?.email} name={otp?.name} bot={otp?.bot}
              onVerified={submitRegistration} onClose={() => { setOtp(null); resetBotShield('client-register'); }} />
            <form onSubmit={handleRegister}>
              {/* Photo de profil */}
              <div className="form-group">
                <label>Photo de profil <span className="required">*</span></label>
                <div className="photo-capture-area">
                  {photoPreview ? (
                    <div className="photo-preview">
                      <img src={photoPreview} alt="Aperçu" />
                      <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem'}}>
                        <button 
                          type="button" 
                          className="change-photo-btn"
                          onClick={() => setShowCameraModal(true)}
                          style={{flex: 1}}
                        >
                          📷 Retake
                        </button>
                        <button 
                          type="button" 
                          className="change-photo-btn"
                          onClick={() => fileInputRef.current.click()}
                          style={{flex: 1}}
                        >
                          📁 Importer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="photo-placeholder"
                      style={{display: 'flex', flexDirection: 'column', gap: '1rem', padding: '2rem'}}
                    >
                      <div style={{textAlign: 'center'}}>
                        <Camera size={40} style={{margin: '0 auto', marginBottom: '0.5rem'}} />
                        <p style={{margin: 0}}>Choisissez une option</p>
                        <small style={{color: '#999'}}>Obligatoire</small>
                      </div>
                      <div style={{display: 'flex', gap: '0.75rem'}}>
                        <button
                          type="button"
                          onClick={() => setShowCameraModal(true)}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: '#FF7800',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                          }}
                        >
                          📷 Prendre une photo
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current.click()}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.95rem'
                          }}
                        >
                          📁 Importer une photo
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoFromFile}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Nom complet <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Jean Mukendi"
                  required
                />
              </div>

              <div className="form-group">
                <label>Numéro de téléphone <span className="required">*</span></label>
                <div className="phone-input-wrapper">
                  <Phone size={20} className="phone-icon" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="+243 XXX XXX XXX"
                    required
                  />
                </div>
                <small className="input-hint">Format: +243 suivi de 9 chiffres</small>
              </div>

              <div className="form-group">
                <label>Email <span className="optional">(facultatif)</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                />
                <small className="input-hint">Optionnel - pour récupération de compte</small>
              </div>

              <div className="form-group">
                <label>Date de naissance <span className="optional">(facultatif)</span></label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
                <small className="input-hint">Optionnel - au format JJ/MM/AAAA</small>
              </div>

              <div className="form-group">
                <label>Mot de passe <span className="required">*</span></label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Au moins 8 caractères"
                  required
                />
              </div>

              <div className="form-group">
                <label>Confirmer le mot de passe <span className="required">*</span></label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Répétez le mot de passe"
                  required
                />
              </div>

              <p style={{ fontSize: '.85rem', color: '#6b7280', margin: '4px 0 12px', lineHeight: 1.5 }}>
                En créant un compte, vous acceptez les{' '}
                <a href="/cgu" target="_blank" rel="noopener noreferrer">Conditions générales d'utilisation</a> et la{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Politique de confidentialité</a> de Mbiyo.
              </p>

              <BotShield formKey="client-register" baseURL={apiService.baseURL} />

              <button type="submit" className="btn-auth" disabled={isSubmitting}>
                {isSubmitting ? '⏳ Inscription en cours...' : 'Créer mon compte'}
              </button>
            </form>

            <p className="auth-switch">
              Déjà un compte ?{' '}
              <span onClick={() => setCurrentPage('login')}>
                Se connecter
              </span>
            </p>
          </div>
        </div>
      </div>
      {showCameraModal && (
        <Stable render={CameraModal} key="CameraModal" 
          onCapture={handlePhotoCaptured}
          onClose={() => setShowCameraModal(false)}
        />
      )}
      </>
    );
  };

  // ============= PAGE MON PROFIL =============
  const ProfilePage = () => {
    const [activeSection, setActiveSection] = useState('info'); // 'info' | 'security'
    const [profileData, setProfileData] = useState({
      name: currentUser?.name || '',
      phone: currentUser?.phone || '',
      email: currentUser?.email || '',
      dateOfBirth: currentUser?.dateOfBirth ? currentUser.dateOfBirth.split('T')[0] : ''
    });
    const [profilePhoto, setProfilePhoto] = useState(currentUser?.photo || null);
    const [showProfileCamera, setShowProfileCamera] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState('');
    const [saveError, setSaveError] = useState('');
    const profilePhotoInputRef = React.useRef(null);

    // Security state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [pwdSuccess, setPwdSuccess] = useState('');
    const [pwdError, setPwdError] = useState('');

    // Security questions state
    const SECURITY_QUESTIONS_LIST = [
      "Quel est le nom de votre premier animal de compagnie ?",
      "Dans quelle ville êtes-vous né(e) ?",
      "Quel est le prénom de votre meilleur(e) ami(e) d'enfance ?",
      "Quel est le nom de jeune fille de votre mère ?",
      "Quel est votre plat préféré ?",
      "Quel est le nom de votre école primaire ?",
      "Quel est votre surnom d'enfance ?",
      "Quel est le nom de votre premier professeur ?"
    ];
    const [secQuestion1, setSecQuestion1] = useState('');
    const [secAnswer1, setSecAnswer1] = useState('');
    const [secQuestion2, setSecQuestion2] = useState('');
    const [secAnswer2, setSecAnswer2] = useState('');
    const [savingQuestions, setSavingQuestions] = useState(false);
    const [questionsSuccess, setQuestionsSuccess] = useState('');
    const [questionsError, setQuestionsError] = useState('');
    const [hasSecurityQuestions, setHasSecurityQuestions] = useState(currentUser?.hasSecurityQuestions || false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Load existing security questions
    React.useEffect(() => {
      if (activeSection === 'security' && currentUser?.user_id) {
        setLoadingQuestions(true);
        apiService.get(`/users/${currentUser.user_id}/security-questions`)
          .then(res => {
            if (res.success && res.questions?.length === 2) {
              setSecQuestion1(res.questions[0].question);
              setSecQuestion2(res.questions[1].question);
            }
          })
          .catch(() => {})
          .finally(() => setLoadingQuestions(false));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const compressProfilePhoto = (imgSrc, callback) => {
      const img = new Image();
      img.src = imgSrc;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxW = 400, maxH = 400;
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; } }
        else { if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; } }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        callback(canvas.toDataURL('image/jpeg', 0.8));
      };
    };

    const handleProfilePhotoFile = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) { setSaveError('Photo trop volumineuse (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        compressProfilePhoto(reader.result, (compressed) => {
          setProfilePhoto(compressed);
          setSaveError('');
        });
      };
      reader.readAsDataURL(file);
    };

    const handleProfilePhotoFromCamera = (photoData) => {
      compressProfilePhoto(photoData, (compressed) => {
        setProfilePhoto(compressed);
        setShowProfileCamera(false);
      });
    };

    const handleProfilePhoneChange = (e) => {
      let value = e.target.value;
      if (!value.startsWith('+243')) value = '+243 ';
      value = value.replace(/[^\d+\s]/g, '');
      const numbers = value.replace(/[^\d]/g, '');
      if (numbers.length > 12) return;
      setProfileData({ ...profileData, phone: value });
    };

    const handleSaveProfile = async () => {
      setSaveError('');
      setSaveSuccess('');
      
      const cleanPhone = profileData.phone.replace(/\s/g, '');
      if (cleanPhone.length < 13) {
        setSaveError('Numéro de téléphone incomplet');
        return;
      }
      if (profileData.email && !profileData.email.includes('@')) {
        setSaveError('Email invalide');
        return;
      }
      if (!profileData.name.trim()) {
        setSaveError('Le nom est obligatoire');
        return;
      }

      setIsSaving(true);
      try {
        const payload = {
          name: profileData.name.trim(),
          phone: cleanPhone,
          email: profileData.email || '',
          dateOfBirth: profileData.dateOfBirth || null
        };
        if (profilePhoto && profilePhoto !== currentUser?.photo) {
          payload.photo = profilePhoto;
        }

        const res = await apiService.put(`/users/${currentUser.user_id}`, payload);
        if (res.success) {
          const updatedUser = { ...currentUser, ...res.user };
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          setSaveSuccess('Profil mis à jour avec succès');
          setTimeout(() => setSaveSuccess(''), 3000);
        } else {
          setSaveError(res.error || 'Erreur lors de la sauvegarde');
        }
      } catch (err) {
        setSaveError(err.error || 'Erreur serveur');
      } finally {
        setIsSaving(false);
      }
    };

    const handleChangePassword = async () => {
      setPwdError('');
      setPwdSuccess('');
      if (!currentPassword) { setPwdError('Entrez votre mot de passe actuel'); return; }
      if (newPassword.length < 8) { setPwdError('Le nouveau mot de passe doit contenir au moins 8 caractères'); return; }
      if (newPassword !== confirmNewPassword) { setPwdError('Les mots de passe ne correspondent pas'); return; }

      setChangingPassword(true);
      try {
        const res = await apiService.post(`/users/${currentUser.user_id}/change-password`, {
          currentPassword,
          newPassword
        });
        if (res.success) {
          setPwdSuccess('Mot de passe modifié avec succès');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmNewPassword('');
          setTimeout(() => setPwdSuccess(''), 3000);
        } else {
          setPwdError(res.error || 'Erreur');
        }
      } catch (err) {
        setPwdError(err.error || 'Erreur serveur');
      } finally {
        setChangingPassword(false);
      }
    };

    const handleSaveSecurityQuestions = async () => {
      setQuestionsError('');
      setQuestionsSuccess('');
      if (!secQuestion1 || !secAnswer1.trim()) { setQuestionsError('Première question et réponse requises'); return; }
      if (!secQuestion2 || !secAnswer2.trim()) { setQuestionsError('Deuxième question et réponse requises'); return; }
      if (secQuestion1 === secQuestion2) { setQuestionsError('Choisissez deux questions différentes'); return; }
      if (secAnswer1.trim().length < 2 || secAnswer2.trim().length < 2) { setQuestionsError('Les réponses doivent contenir au moins 2 caractères'); return; }

      setSavingQuestions(true);
      try {
        const res = await apiService.post(`/users/${currentUser.user_id}/security-questions`, {
          questions: [
            { question: secQuestion1, answer: secAnswer1.trim() },
            { question: secQuestion2, answer: secAnswer2.trim() }
          ]
        });
        if (res.success) {
          setQuestionsSuccess('Questions de sécurité enregistrées');
          setHasSecurityQuestions(true);
          setSecAnswer1('');
          setSecAnswer2('');
          const updatedUser = { ...currentUser, hasSecurityQuestions: true };
          setCurrentUser(updatedUser);
          localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          setTimeout(() => setQuestionsSuccess(''), 3000);
        } else {
          setQuestionsError(res.error || 'Erreur');
        }
      } catch (err) {
        setQuestionsError(err.error || 'Erreur serveur');
      } finally {
        setSavingQuestions(false);
      }
    };

    const sectionBtnStyle = (active) => ({
      flex: 1, padding: '0.75rem', border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '0.9rem',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
      background: active ? '#F54119' : '#F4F5F7', color: active ? 'white' : '#5C636E',
      transition: 'all 0.2s', fontFamily: 'inherit'
    });

    const cardStyle = {
      background: 'white', borderRadius: '16px', padding: '1.5rem', marginBottom: '1rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #F4F5F7'
    };

    const labelStyle = { display: 'block', fontWeight: '600', color: '#414852', marginBottom: '0.4rem', fontSize: '0.9rem' };

    const inputStyle = {
      width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '12px',
      fontSize: '1rem', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
      transition: 'border-color 0.2s'
    };

    const btnPrimaryStyle = {
      width: '100%', padding: '0.85rem', background: '#F54119', color: 'white', border: 'none',
      borderRadius: '12px', fontWeight: '600', fontSize: '1rem', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontFamily: 'inherit'
    };

    return (
      <>
      <div style={{ padding: '0 0 2rem 0' }}>
        <button onClick={() => setCurrentPage('home')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#F54119', cursor: 'pointer', fontSize: '0.95rem', padding: '0.5rem 0', marginBottom: '0.75rem', fontFamily: 'inherit', fontWeight: '500' }}>
          <ArrowLeft size={20} /> Retour
        </button>

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#1E2229' }}>Mon Profil</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#5C636E', fontSize: '0.9rem' }}>Gérez vos informations et votre sécurité</p>
        </div>

        {/* Section tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', background: '#FFFFFF', padding: '0.25rem', borderRadius: '14px' }}>
          <button style={sectionBtnStyle(activeSection === 'info')} onClick={() => setActiveSection('info')}>
            <UserIcon size={18} /> Informations
          </button>
          <button style={sectionBtnStyle(activeSection === 'security')} onClick={() => setActiveSection('security')}>
            <ShieldCheck size={18} /> Sécurité
          </button>
        </div>

        {/* ===== SECTION INFORMATIONS ===== */}
        {activeSection === 'info' && (
          <div>
            {saveSuccess && (
              <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                <CheckCircle size={18} /> {saveSuccess}
              </div>
            )}
            {saveError && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                <AlertCircle size={18} /> {saveError}
              </div>
            )}

            {/* Photo */}
            <div style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
                <div style={{
                  width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden',
                  background: '#E9EBEF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '3px solid #F54119', margin: '0 auto'
                }}>
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <UserIcon size={40} color="#9AA0A8" />
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                <button onClick={() => setShowProfileCamera(true)} style={{ padding: '0.5rem 1rem', background: '#F54119', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                  <Camera size={16} /> Caméra
                </button>
                <button onClick={() => profilePhotoInputRef.current?.click()} style={{ padding: '0.5rem 1rem', background: '#F4F5F7', color: '#414852', border: '1px solid #E9EBEF', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                  <Edit3 size={16} /> Importer
                </button>
                <input ref={profilePhotoInputRef} type="file" accept="image/*" onChange={handleProfilePhotoFile} style={{ display: 'none' }} />
              </div>
            </div>

            {/* Nom */}
            <div style={cardStyle}>
              <label style={labelStyle}><UserIcon size={16} style={{ verticalAlign: '-3px', marginRight: '0.3rem' }} />Nom complet</label>
              <input style={inputStyle} value={profileData.name} onChange={e => setProfileData({ ...profileData, name: e.target.value })} placeholder="Votre nom" />
            </div>

            {/* Téléphone */}
            <div style={cardStyle}>
              <label style={labelStyle}><Phone size={16} style={{ verticalAlign: '-3px', marginRight: '0.3rem' }} />Numéro de téléphone</label>
              <input style={inputStyle} type="tel" value={profileData.phone} onChange={handleProfilePhoneChange} placeholder="+243 XXX XXX XXX" />
              <small style={{ color: '#9AA0A8', fontSize: '0.8rem' }}>Format: +243 suivi de 9 chiffres</small>
            </div>

            {/* Email */}
            <div style={cardStyle}>
              <label style={labelStyle}><Mail size={16} style={{ verticalAlign: '-3px', marginRight: '0.3rem' }} />Adresse email</label>
              <input style={inputStyle} type="email" value={profileData.email} onChange={e => setProfileData({ ...profileData, email: e.target.value })} placeholder="exemple@email.com (optionnel)" />
              {!profileData.email && (
                <small style={{ color: '#FFB900', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.3rem' }}>
                  <AlertCircle size={14} /> Ajoutez votre email pour la récupération du compte
                </small>
              )}
            </div>

            {/* Date de naissance */}
            <div style={cardStyle}>
              <label style={labelStyle}><Calendar size={16} style={{ verticalAlign: '-3px', marginRight: '0.3rem' }} />Date de naissance</label>
              <input style={inputStyle} type="date" value={profileData.dateOfBirth} onChange={e => setProfileData({ ...profileData, dateOfBirth: e.target.value })} />
            </div>

            {/* Bouton sauvegarder */}
            <button onClick={handleSaveProfile} disabled={isSaving} style={{ ...btnPrimaryStyle, opacity: isSaving ? 0.7 : 1 }}>
              {isSaving ? <Loader size={18} className="spin" /> : <Save size={18} />}
              {isSaving ? 'Sauvegarde...' : 'Enregistrer les modifications'}
            </button>

          </div>
        )}

        {/* ===== SECTION SÉCURITÉ ===== */}
        {activeSection === 'security' && (
          <div>
            {/* Changer mot de passe */}
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#1E2229', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Lock size={20} color="#F54119" /> Modifier le mot de passe
              </h3>

              {pwdSuccess && (
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <CheckCircle size={18} /> {pwdSuccess}
                </div>
              )}
              {pwdError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <AlertCircle size={18} /> {pwdError}
                </div>
              )}

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelStyle}>Mot de passe actuel</label>
                <div style={{ position: 'relative' }}>
                  <input style={inputStyle} type={showCurrentPwd ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" />
                  <button onClick={() => setShowCurrentPwd(!showCurrentPwd)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9AA0A8' }}>
                    {showCurrentPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={labelStyle}>Nouveau mot de passe</label>
                <div style={{ position: 'relative' }}>
                  <input style={inputStyle} type={showNewPwd ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 8 caractères" />
                  <button onClick={() => setShowNewPwd(!showNewPwd)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9AA0A8' }}>
                    {showNewPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Confirmer le nouveau mot de passe</label>
                <input style={inputStyle} type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} placeholder="••••••••" />
              </div>

              <button onClick={handleChangePassword} disabled={changingPassword} style={{ ...btnPrimaryStyle, background: '#FFB900', opacity: changingPassword ? 0.7 : 1 }}>
                {changingPassword ? <Loader size={18} className="spin" /> : <KeyRound size={18} />}
                {changingPassword ? 'Modification...' : 'Changer le mot de passe'}
              </button>
            </div>

            {/* Questions de sécurité */}
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: '#1E2229', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <HelpCircle size={20} color="#F54119" /> Questions de sécurité
              </h3>
              <p style={{ margin: '0 0 1rem', color: '#5C636E', fontSize: '0.85rem' }}>
                Ces questions peuvent servir à réinitialiser votre compte si vous n'avez pas d'adresse email.
              </p>

              {hasSecurityQuestions && !questionsSuccess && (
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.6rem 0.75rem', borderRadius: '10px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <CheckCircle size={16} /> Questions de sécurité configurées — vous pouvez les modifier ci-dessous
                </div>
              )}

              {questionsSuccess && (
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <CheckCircle size={18} /> {questionsSuccess}
                </div>
              )}
              {questionsError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                  <AlertCircle size={18} /> {questionsError}
                </div>
              )}

              {loadingQuestions ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#9AA0A8' }}><Loader size={20} className="spin" /> Chargement...</div>
              ) : (
                <>
                  {/* Question 1 */}
                  <div style={{ marginBottom: '1rem', background: '#FFFFFF', padding: '1rem', borderRadius: '12px' }}>
                    <label style={{ ...labelStyle, color: '#F54119' }}>Question 1</label>
                    <select
                      value={secQuestion1}
                      onChange={e => setSecQuestion1(e.target.value)}
                      style={{ ...inputStyle, appearance: 'auto', cursor: 'pointer' }}
                    >
                      <option value="">-- Choisir une question --</option>
                      {SECURITY_QUESTIONS_LIST.filter(q => q !== secQuestion2).map(q => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={labelStyle}>Votre réponse</label>
                      <input style={inputStyle} value={secAnswer1} onChange={e => setSecAnswer1(e.target.value)} placeholder="Tapez votre réponse..." />
                    </div>
                  </div>

                  {/* Question 2 */}
                  <div style={{ marginBottom: '1rem', background: '#FFFFFF', padding: '1rem', borderRadius: '12px' }}>
                    <label style={{ ...labelStyle, color: '#F54119' }}>Question 2</label>
                    <select
                      value={secQuestion2}
                      onChange={e => setSecQuestion2(e.target.value)}
                      style={{ ...inputStyle, appearance: 'auto', cursor: 'pointer' }}
                    >
                      <option value="">-- Choisir une question --</option>
                      {SECURITY_QUESTIONS_LIST.filter(q => q !== secQuestion1).map(q => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={labelStyle}>Votre réponse</label>
                      <input style={inputStyle} value={secAnswer2} onChange={e => setSecAnswer2(e.target.value)} placeholder="Tapez votre réponse..." />
                    </div>
                  </div>

                  <button onClick={handleSaveSecurityQuestions} disabled={savingQuestions} style={{ ...btnPrimaryStyle, background: '#10b981', opacity: savingQuestions ? 0.7 : 1 }}>
                    {savingQuestions ? <Loader size={18} className="spin" /> : <ShieldCheck size={18} />}
                    {savingQuestions ? 'Enregistrement...' : (hasSecurityQuestions ? 'Modifier les questions' : 'Enregistrer les questions')}
                  </button>
                </>
              )}
            </div>

            {/* Info sécurité */}
            <div style={{ ...cardStyle, background: '#FEE9E2', border: '1px solid #bfdbfe' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#C9350F', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                Pour une sécurité optimale, ajoutez votre adresse email et configurez vos questions de sécurité. Cela vous permettra de récupérer votre compte en cas de perte du mot de passe.
              </p>
            </div>
          </div>
        )}

        {/* Déconnexion — toujours accessible depuis le profil (le menu l'a aussi, tout en bas) */}
        <button
          onClick={() => {
            authService.logout();
            setIsAuthenticated(false);
            setCurrentUser(null);
            setCurrentPage('login');
          }}
          style={{ width: '100%', marginTop: '1.5rem', padding: '0.9rem', background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: '14px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
        >
          <XCircle size={18} /> Se déconnecter
        </button>
      </div>

      {showProfileCamera && (
        <Stable render={CameraModal} key="CameraModal"
          onCapture={handleProfilePhotoFromCamera}
          onClose={() => setShowProfileCamera(false)}
        />
      )}
      </>
    );
  };

  const backButtonStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#F54119', cursor: 'pointer', fontSize: '0.95rem', padding: '0.5rem 0', marginBottom: '0.75rem', fontFamily: 'inherit', fontWeight: '500' };

  const BackButton = ({ to = 'home' }) => (
    <button onClick={() => setCurrentPage(to)} style={backButtonStyle}>
      <ArrowLeft size={20} />
      Retour
    </button>
  );

  const QRCodePage = () => {
    if (!activeSubscription) {
      return (
        <div className="qr-page">
          <Stable render={BackButton} key="BackButton" />
          <div className="qr-header">
            <h2>{t.myQR}</h2>
            <div className="status-badge" style={{background: '#fee2e2', color: '#991b1b'}}>
              <XCircle size={16} />
              {t.subscriptionExpired}
            </div>
          </div>
          <div className="qr-container">
            <div className="qr-card" style={{border: '2px solid #DC2626'}}>
              <p style={{textAlign: 'center', color: '#991b1b', fontSize: '1.1rem'}}>❌ Vous n'avez pas d'abonnement actif</p>
              <p style={{textAlign: 'center', color: '#666', marginTop: '1rem'}}>Veuillez choisir une formule pour accéder aux trajets Mbiyo</p>
              <button className="btn-primary" onClick={() => setCurrentPage('subscription')} style={{background: '#DC2626', marginTop: '1.5rem'}}>
                {t.selectPlan}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="qr-page">
        <Stable render={BackButton} key="BackButton" />
        <div className="qr-header">
          <h2>{t.myQR}</h2>
          <div className="status-badge active" style={{background: '#d1fae5', color: '#065f46'}}>
            <CheckCircle size={16} />
            {t.activeUntil} {user.expiryDate}
          </div>
        </div>

        <div className="qr-container">
          <div className="qr-card">
            <div className="qr-user-info">
              <div className="user-avatar" style={{background: user.planColor}}>{user.name.charAt(0)}</div>
              <div>
                <h3>{user.name}</h3>
                <p className="plan-badge" style={{background: user.planColor, color: 'white'}}>{user.plan}</p>
              </div>
            </div>

            <div className="qr-code-display" data-qr-value={user.qrCode}>
              <div style={{display: 'flex', justifyContent: 'center', marginBottom: '1rem'}}>
                <div style={{background: 'white', padding: '1rem', borderRadius: '8px', border: `2px solid ${user.planColor}`}}>
                  {user.qrCodeImage ? (
                    <img src={user.qrCodeImage} alt="QR Code" style={{width: '200px', height: '200px'}} />
                  ) : (
                    <QrCode size={200} strokeWidth={1} color={user.planColor} />
                  )}
                </div>
              </div>
              <div className="qr-code-text" style={{fontSize: '0.8rem', color: '#666', wordBreak: 'break-all'}}>{user.qrCode}</div>
            </div>

            <div className="qr-instructions">
              <p style={{color: user.planColor, fontWeight: '600'}}>✓ {t.showDriver}</p>
            </div>

            <div className="qr-validity">
              <Clock size={18} color={user.planColor} />
              <span style={{color: user.planColor, fontWeight: '600'}}>{user.daysLeft} {t.daysLeft}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SubscriptionPage = () => {
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [message, setMessage] = useState('');
    const [showRenewOptions, setShowRenewOptions] = useState(false);

    // Merge API prices into subscriptionPlans
    const mergedPlans = subscriptionPlans.map(plan => {
      if (planPrices && planPrices[plan.id]) {
        const p = planPrices[plan.id];
        return {
          ...plan,
          monthlyPrice: p.monthly != null ? String(p.monthly) : plan.monthlyPrice,
          yearlyPrice: p.yearly != null ? String(p.yearly) : plan.yearlyPrice,
        };
      }
      return plan;
    });

    // Gérer le succès du paiement (callback depuis PaymentModal)
    const handlePaymentSuccess = async (paymentData) => {
      try {
        await fetchUserSubscription(currentUser.user_id);
        setMessage(`✅ Paiement réussi! Votre abonnement ${paymentData.planName} a été activé 🎉`);
        setShowPaymentModal(false);
        setTimeout(() => setCurrentPage('home'), 2000);
      } catch (error) {
        setMessage('❌ Paiement enregistré mais erreur lors du rechargement du profil');
      }
    };

    const handleSubscribe = (plan) => {
      if (!currentUser) { setMessage(t.loginRequired || 'Veuillez vous connecter'); return; }
      setSelectedPlanForPayment(plan);
      setSelectedBillingCycle(billingCycle);
      setShowPaymentModal(true);
    };

    const daysLeft = getDaysLeft();
    const hasActiveSub = !!activeSubscription;
    const planInfo = hasActiveSub ? PLAN_MAPPING[activeSubscription.plan_type] : null;
    const expiryStr = hasActiveSub ? new Date(activeSubscription.valid_until).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

    // — Écran abonnement actif —
    if (hasActiveSub && !showRenewOptions) {
      return (
        <div style={{ minHeight: '100vh', background: '#FFFFFF', paddingBottom: '2rem' }}>
          <div style={{ background: `linear-gradient(135deg, ${planInfo?.color || '#FF7800'}, ${planInfo?.color || '#C9350F'}cc)`, padding: '1.5rem 1rem 2.5rem', color: 'white' }}>
            <button onClick={() => setCurrentPage('home')} style={{ background: 'none', border: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', marginBottom: '1rem', padding: 0 }}>
              <ArrowLeft size={20} /> Retour
            </button>
            <p style={{ opacity: 0.85, fontSize: '0.8rem', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Abonnement actif</p>
            <h1 style={{ margin: '0 0 4px', fontSize: '2rem', fontWeight: '800' }}>{planInfo?.name || activeSubscription.plan_type?.toUpperCase()}</h1>
            <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>{planInfo?.description}</p>
          </div>

          <div style={{ padding: '0 1rem', marginTop: '-1rem' }}>
            {/* Carte jours restants */}
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '1.5rem', marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${planInfo?.color || '#FF7800'}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Calendar size={28} style={{ color: planInfo?.color || '#FF7800' }} />
              </div>
              <div>
                <p style={{ margin: '0 0 2px', fontSize: '2rem', fontWeight: '800', color: daysLeft <= 7 ? '#DC2626' : '#1C1C1C' }}>{daysLeft}</p>
                <p style={{ margin: 0, color: '#5C636E', fontSize: '0.85rem' }}>jours restants · Expire le {expiryStr}</p>
              </div>
            </div>

            {/* Barre de progression */}
            {activeSubscription.valid_from && (
              (() => {
                const total = new Date(activeSubscription.valid_until) - new Date(activeSubscription.valid_from);
                const elapsed = Date.now() - new Date(activeSubscription.valid_from);
                const pct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
                const remaining = 100 - pct;
                return (
                  <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '1.25rem', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#5C636E' }}>Utilisation</span>
                      <span style={{ fontSize: '0.8rem', fontWeight: '600', color: remaining <= 20 ? '#DC2626' : '#414852' }}>{remaining}% restant</span>
                    </div>
                    <div style={{ height: '8px', background: '#F4F5F7', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: daysLeft <= 7 ? '#DC2626' : (planInfo?.color || '#FF7800'), borderRadius: '99px', transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })()
            )}

            {/* Alerte renouvellement */}
            {daysLeft <= 7 && (
              <div style={{ background: '#fef2f2', border: '1px solid #FEE2E2', borderRadius: '12px', padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertCircle size={20} color="#DC2626" />
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#991b1b', fontWeight: '600' }}>
                  Votre abonnement expire dans {daysLeft} jour{daysLeft !== 1 ? 's' : ''} !
                </p>
              </div>
            )}

            {message && (
              <div style={{ padding: '1rem', marginBottom: '1rem', borderRadius: '12px', background: message.includes('✅') ? '#d1fae5' : '#fee2e2', color: message.includes('✅') ? '#065f46' : '#991b1b', border: `1px solid ${message.includes('✅') ? '#6ee7b7' : '#FEE2E2'}` }}>
                {message}
              </div>
            )}

            <button
              onClick={() => setShowRenewOptions(true)}
              style={{ width: '100%', padding: '16px', background: planInfo?.color || '#FF7800', color: 'white', border: 'none', borderRadius: '14px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', marginBottom: '12px' }}
            >
              🔄 Renouveler / Changer de formule
            </button>
            <button
              onClick={() => setCurrentPage('home')}
              style={{ width: '100%', padding: '14px', background: 'white', color: '#414852', border: '1px solid #E9EBEF', borderRadius: '14px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Retour à l'accueil
            </button>
          </div>

          {selectedPlanForPayment && (
            <PaymentModal
              isOpen={showPaymentModal}
              planName={selectedPlanForPayment.name}
              amount={selectedBillingCycle === 'monthly' ? parseInt(selectedPlanForPayment.monthlyPrice) : parseInt(selectedPlanForPayment.yearlyPrice)}
              duration={selectedBillingCycle === 'monthly' ? 30 : 365}
              onSuccess={handlePaymentSuccess}
              onCancel={() => { setShowPaymentModal(false); setSelectedPlanForPayment(null); }}
            />
          )}
        </div>
      );
    }

    // — Écran choix de plan —
    return (
      <div className="subscription-page">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
          {hasActiveSub && (
            <button onClick={() => setShowRenewOptions(false)} style={backButtonStyle}>
              <ArrowLeft size={20} /> Retour
            </button>
          )}
          {!hasActiveSub && <Stable render={BackButton} key="BackButton" />}
          {hasActiveSub && <span style={{ fontSize: '0.85rem', color: '#5C636E' }}>Changer de formule</span>}
        </div>

        {!hasActiveSub && <h2>{t.selectPlan}</h2>}

        {message && (
          <div className="subscription-message" style={{
            padding: '1rem', marginBottom: '1rem', borderRadius: '8px',
            background: message.includes('✅') ? '#d1fae5' : '#fee2e2',
            color: message.includes('✅') ? '#065f46' : '#991b1b',
            border: `1px solid ${message.includes('✅') ? '#6ee7b7' : '#FEE2E2'}`
          }}>{message}</div>
        )}

        <div className="billing-toggle">
          <button className={billingCycle === 'monthly' ? 'active' : ''} onClick={() => setBillingCycle('monthly')}>{t.monthly}</button>
          <button className={billingCycle === 'yearly' ? 'active' : ''} onClick={() => setBillingCycle('yearly')}>
            {t.yearly} <span className="save-badge">{t.save} 10%</span>
          </button>
        </div>

        <div className="plans-grid">
          {mergedPlans.map(plan => {
            const Icon = plan.icon;
            const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
            const period = billingCycle === 'monthly' ? t.perMonth : t.perYear;
            const isCurrentPlan = hasActiveSub && activeSubscription.plan_type === plan.id;

            return (
              <div key={plan.id} className="plan-card" style={{ borderTopColor: plan.color, opacity: 1, position: 'relative' }}>
                {isCurrentPlan && (
                  <div style={{ position: 'absolute', top: '10px', right: '10px', background: plan.color, color: 'white', borderRadius: '20px', padding: '2px 10px', fontSize: '0.7rem', fontWeight: '700' }}>
                    ACTUEL
                  </div>
                )}
                <div className="plan-icon" style={{background: `${plan.color}20`, color: plan.color}}><Icon size={24} /></div>
                <h3>{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="plan-price">
                  <span className="price">{parseInt(price).toLocaleString('fr-FR')}</span>
                  <span className="currency">FC</span>
                  <span className="period">{period}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p style={{fontSize: '0.75rem', color: '#999', margin: '-0.4rem 0 0.8rem 0', textAlign: 'center'}}>
                    Au lieu de: {(parseInt(plan.monthlyPrice) * 12).toLocaleString('fr-FR')} FC
                  </p>
                )}
                <ul className="plan-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}><CheckCircle size={16} style={{color: plan.color}} />{feature}</li>
                  ))}
                </ul>
                <button
                  className="subscribe-btn"
                  style={{background: plan.color}}
                  onClick={() => handleSubscribe(plan)}
                >
                  {isCurrentPlan ? '🔄 Renouveler' : t.subscribe}
                </button>
              </div>
            );
          })}
        </div>

        {selectedPlanForPayment && (
          <PaymentModal
            isOpen={showPaymentModal}
            planName={selectedPlanForPayment.name}
            amount={selectedBillingCycle === 'monthly' ? parseInt(selectedPlanForPayment.monthlyPrice) : parseInt(selectedPlanForPayment.yearlyPrice)}
            duration={selectedBillingCycle === 'monthly' ? 30 : 365}
            onSuccess={handlePaymentSuccess}
            onCancel={() => { setShowPaymentModal(false); setSelectedPlanForPayment(null); setSelectedBillingCycle('monthly'); }}
          />
        )}
      </div>
    );
  };

  const RoutesPage = () => (
    <div className="routes-page">
      <Stable render={BackButton} key="BackButton" />
      <h2>{t.availableRoutes}</h2>
      {loadingRoutes ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader size={32} className="spinning" />
          <p>Chargement des lignes...</p>
        </div>
      ) : routes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#5C636E' }}>
          <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p>Aucune ligne disponible pour le moment</p>
        </div>
      ) : (
        <div className="routes-list">
          {routes.map(route => (
            <div key={route.route_id} className={`route-card ${route.active ? 'active' : 'inactive'}`}>
              <div className="route-icon">
                <MapPin size={24} />
              </div>
              <div className="route-info">
                <h3>{route.name}</h3>
                <div className="route-meta">
                  {route.departure && <span>{route.departure.name}</span>}
                  <span>→</span>
                  {route.arrival && <span>{route.arrival.name}</span>}
                  {route.bidirectional && (
                    <span style={{ fontSize: '11px', color: '#5C636E', marginLeft: '8px' }}>↔ Bidirectionnel</span>
                  )}
                </div>
              </div>
              <div className="route-status">
                {route.active ? (
                  <CheckCircle size={20} color="#10b981" />
                ) : (
                  <XCircle size={20} color="#DC2626" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ============================================================
  // PAGE SOLDE
  // ============================================================
  const BalancePage = () => {
    const [period, setPeriod] = useState('month');
    const [transactions, setTransactions] = useState([]);
    const [loadingTx, setLoadingTx] = useState(false);
    const [balance, setBalance] = useState(userBalance);
    const [refreshing, setRefreshing] = useState(false);

    // Recharge state
    const [rechargeStep, setRechargeStep] = useState('idle'); // idle | amount | telecom | phone | processing | success | error
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [rechargeTelecom, setRechargeTelecom] = useState('');
    const [rechargePhone, setRechargePhone] = useState('+243 ');
    const [rechargeError, setRechargeError] = useState('');
    const [rechargePaymentId, setRechargePaymentId] = useState(null);
    const [pollCount, setPollCount] = useState(0);
    const pollRef = useRef(null);

    const loadTransactions = async (p) => {
      if (!currentUser?.user_id) return;
      setLoadingTx(true);
      try {
        const res = await apiService.get(`/balance/user/${currentUser.user_id}/transactions?period=${p}`);
        if (res.success) setTransactions(res.transactions || []);
      } catch (_) {}
      finally { setLoadingTx(false); }
    };

    const refreshBalance = async () => {
      if (!currentUser?.user_id) return;
      setRefreshing(true);
      try {
        const res = await apiService.get(`/balance/user/${currentUser.user_id}`);
        if (res.success) {
          const b = res.balance ?? res;
          setBalance(b);
          setUserBalance(b);
        }
      } catch (_) {}
      finally { setRefreshing(false); }
    };

    useEffect(() => {
      loadTransactions(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => {
      if (!balance && currentUser?.user_id) refreshBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll payment after initiation
    useEffect(() => {
      if (rechargeStep === 'processing' && rechargePaymentId) {
        let tries = 0;
        pollRef.current = setInterval(async () => {
          tries++;
          setPollCount(tries);
          if (tries > 24) {
            clearInterval(pollRef.current);
            setRechargeStep('error');
            setRechargeError('Délai dépassé. Vérifiez votre téléphone.');
            return;
          }
          try {
            const res = await apiService.get(`/payments/${rechargePaymentId}`);
            const status = res.payment?.status || res.status;
            if (status === 'completed' || status === 'success') {
              clearInterval(pollRef.current);
              setRechargeStep('success');
              refreshBalance();
              loadTransactions(period);
            } else if (status === 'failed' || status === 'cancelled') {
              clearInterval(pollRef.current);
              setRechargeStep('error');
              setRechargeError('Paiement refusé ou annulé.');
            }
          } catch (_) {}
        }, 5000);
        return () => clearInterval(pollRef.current);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rechargeStep, rechargePaymentId]);

    const initiateRecharge = async () => {
      if (!rechargeAmount || isNaN(parseInt(rechargeAmount)) || parseInt(rechargeAmount) < 500) {
        setRechargeError('Montant minimum : 500 FC');
        return;
      }
      setRechargeStep('telecom');
      setRechargeError('');
    };

    const selectTelecom = (t) => {
      setRechargeTelecom(t);
      setRechargeStep('phone');
    };

    const submitRecharge = async () => {
      const cleanPhone = rechargePhone.replace(/\s/g, '');
      if (cleanPhone.length < 12) { setRechargeError('Numéro incomplet'); return; }
      setRechargeError('');
      setRechargeStep('processing');
      try {
        const res = await apiService.post('/balance/user/' + currentUser.user_id + '/topup/validate', {
          amount: parseInt(rechargeAmount),
          phone_number: cleanPhone,
          telecom: rechargeTelecom
        });
        if (res.success && res.payment_id) {
          setRechargePaymentId(res.payment_id);
          // Wait for confirm call
          const res2 = await apiService.post('/balance/user/' + currentUser.user_id + '/topup', {
            payment_id: res.payment_id,
            amount: parseInt(rechargeAmount),
            phone_number: cleanPhone,
            telecom: rechargeTelecom
          });
          if (!res2.success) { setRechargeStep('error'); setRechargeError(res2.error || 'Erreur de recharge'); }
        } else if (res.success) {
          // Direct success
          setRechargeStep('success');
          refreshBalance();
          loadTransactions(period);
        } else {
          setRechargeStep('error');
          setRechargeError(res.error || 'Erreur lors de la recharge');
        }
      } catch (err) {
        setRechargeStep('error');
        setRechargeError(err.message || 'Erreur réseau');
      }
    };

    const txTypeInfo = (type) => {
      switch(type) {
        case 'topup': return { label: 'Recharge', icon: <ArrowDownCircle size={18} color="#10b981" />, color: '#10b981', sign: '+' };
        case 'ride_payment': return { label: 'Trajet', icon: <Car size={18} color="#DC2626" />, color: '#DC2626', sign: '-' };
        case 'cancellation_fee': return { label: 'Annulation', icon: <XCircle size={18} color="#FFB900" />, color: '#FFB900', sign: '-' };
        case 'refund': return { label: 'Remboursement', icon: <ArrowUpCircle size={18} color="#FF7800" />, color: '#FF7800', sign: '+' };
        case 'debt_recovery': return { label: 'Recouvrement', icon: <RotateCcw size={18} color="#5C636E" />, color: '#5C636E', sign: '-' };
        case 'admin_adjustment': return { label: 'Ajustement', icon: <Settings size={18} color="#FF9B00" />, color: '#FF9B00', sign: '+/-' };
        default: return { label: type || 'Transaction', icon: <DollarSign size={18} color="#5C636E" />, color: '#5C636E', sign: '' };
      }
    };

    const formatAmount = (amount) => new Intl.NumberFormat('fr-FR').format(Math.abs(amount));

    const cardStyle = { background: 'white', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '1.25rem', marginBottom: '1rem' };

    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF', paddingBottom: '2rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #FF7800, #C9350F)', padding: '1.5rem 1rem 2.5rem', color: 'white' }}>
          <button onClick={() => setCurrentPage('home')} style={{ background: 'none', border: 'none', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', cursor: 'pointer', marginBottom: '1rem', padding: 0 }}>
            <ArrowLeft size={20} /> Retour
          </button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ opacity: 0.8, fontSize: '0.85rem', margin: '0 0 4px' }}>Votre solde</p>
              <p style={{ fontSize: '2.2rem', fontWeight: '800', margin: 0 }}>
                {balance != null ? formatAmount(typeof balance === 'object' ? (balance.amount ?? balance.balance ?? 0) : balance) : '---'} <span style={{ fontSize: '1rem', fontWeight: '400' }}>FC</span>
              </p>
            </div>
            <button onClick={refreshBalance} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
              <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
            </button>
          </div>
        </div>

        <div style={{ padding: '0 1rem', marginTop: '-1.5rem' }}>
          {/* Bouton Recharger */}
          {rechargeStep === 'idle' && (
            <div style={cardStyle}>
              <button
                onClick={() => { setRechargeStep('amount'); setRechargeError(''); }}
                style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #FF7800, #C9350F)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <PlusCircle size={20} /> Recharger mon solde
              </button>
            </div>
          )}

          {/* Étape 1 : Montant */}
          {rechargeStep === 'amount' && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '700' }}>Montant à recharger</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '1rem' }}>
                {['2000', '5000', '10000', '20000', '50000', '100000'].map(amt => (
                  <button key={amt} onClick={() => setRechargeAmount(amt)} style={{ padding: '10px', border: `2px solid ${rechargeAmount === amt ? '#FF7800' : '#E9EBEF'}`, borderRadius: '10px', background: rechargeAmount === amt ? '#FEE9E2' : 'white', color: rechargeAmount === amt ? '#FF7800' : '#414852', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>
                    {formatAmount(parseInt(amt))} FC
                  </button>
                ))}
              </div>
              <input type="number" placeholder="Autre montant (FC)" value={rechargeAmount} onChange={e => setRechargeAmount(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #E9EBEF', borderRadius: '10px', fontSize: '1rem', boxSizing: 'border-box', marginBottom: '8px' }} />
              {rechargeError && <p style={{ color: '#DC2626', fontSize: '0.85rem', margin: '0 0 8px' }}>{rechargeError}</p>}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setRechargeStep('idle')} style={{ flex: 1, padding: '12px', border: '1px solid #E9EBEF', borderRadius: '10px', background: 'white', cursor: 'pointer', fontWeight: '600' }}>Annuler</button>
                <button onClick={initiateRecharge} style={{ flex: 2, padding: '12px', background: '#FF7800', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}>Continuer</button>
              </div>
            </div>
          )}

          {/* Étape 2 : Opérateur */}
          {rechargeStep === 'telecom' && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '700' }}>Choisissez votre opérateur</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[{ id: 'airtel', label: 'Airtel Money', color: '#DC2626' }, { id: 'mpesa', label: 'M-Pesa', color: '#10b981' }, { id: 'orange', label: 'Orange Money', color: '#FFB900' }].map(op => (
                  <button key={op.id} onClick={() => selectTelecom(op.id)} style={{ padding: '14px', border: `2px solid ${op.color}`, borderRadius: '12px', background: '#fff', color: op.color, fontWeight: '700', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: op.color, display: 'inline-block' }} /> {op.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setRechargeStep('amount')} style={{ width: '100%', marginTop: '12px', padding: '12px', border: '1px solid #E9EBEF', borderRadius: '10px', background: 'white', cursor: 'pointer', fontWeight: '600' }}>Retour</button>
            </div>
          )}

          {/* Étape 3 : Numéro */}
          {rechargeStep === 'phone' && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: '700' }}>Numéro {rechargeTelecom}</h3>
              <p style={{ margin: '0 0 1rem', color: '#5C636E', fontSize: '0.85rem' }}>Montant : {formatAmount(parseInt(rechargeAmount))} FC</p>
              <input
                type="tel"
                placeholder="+243 8XX XXX XXX"
                value={rechargePhone}
                onChange={e => {
                  let v = e.target.value;
                  if (!v.startsWith('+243')) v = '+243 ';
                  v = v.replace(/[^\d+\s]/g, '');
                  setRechargePhone(v);
                }}
                style={{ width: '100%', padding: '14px', border: '1px solid #E9EBEF', borderRadius: '10px', fontSize: '1.1rem', boxSizing: 'border-box', marginBottom: '8px' }}
              />
              {rechargeError && <p style={{ color: '#DC2626', fontSize: '0.85rem', margin: '0 0 8px' }}>{rechargeError}</p>}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setRechargeStep('telecom')} style={{ flex: 1, padding: '12px', border: '1px solid #E9EBEF', borderRadius: '10px', background: 'white', cursor: 'pointer', fontWeight: '600' }}>Retour</button>
                <button onClick={submitRecharge} style={{ flex: 2, padding: '12px', background: '#FF7800', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}>Payer</button>
              </div>
            </div>
          )}

          {/* En cours */}
          {rechargeStep === 'processing' && (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '2rem' }}>
              <Loader size={40} className="spinning" style={{ color: '#FF7800', marginBottom: '1rem' }} />
              <p style={{ fontWeight: '700', fontSize: '1rem' }}>Vérification du paiement...</p>
              <p style={{ color: '#5C636E', fontSize: '0.85rem' }}>Tentative {pollCount}/24 · Validez sur votre téléphone</p>
            </div>
          )}

          {/* Succès */}
          {rechargeStep === 'success' && (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '2rem' }}>
              <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
              <p style={{ fontWeight: '700', fontSize: '1.1rem', color: '#10b981' }}>Recharge réussie !</p>
              <p style={{ color: '#5C636E', fontSize: '0.85rem', marginBottom: '1.5rem' }}>+{formatAmount(parseInt(rechargeAmount))} FC ajoutés à votre solde</p>
              <button onClick={() => setRechargeStep('idle')} style={{ padding: '12px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700' }}>OK</button>
            </div>
          )}

          {/* Erreur */}
          {rechargeStep === 'error' && (
            <div style={{ ...cardStyle, textAlign: 'center', padding: '2rem' }}>
              <XCircle size={48} color="#DC2626" style={{ marginBottom: '1rem' }} />
              <p style={{ fontWeight: '700', fontSize: '1rem', color: '#DC2626' }}>Paiement échoué</p>
              <p style={{ color: '#5C636E', fontSize: '0.85rem', marginBottom: '1.5rem' }}>{rechargeError}</p>
              <button onClick={() => setRechargeStep('idle')} style={{ padding: '12px 24px', background: '#FF7800', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700' }}>Réessayer</button>
            </div>
          )}

          {/* Historique des transactions */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Historique</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['week', 'month', 'year'].map(p => (
                  <button key={p} onClick={() => setPeriod(p)} style={{ padding: '4px 10px', border: 'none', borderRadius: '20px', background: period === p ? '#FF7800' : '#F4F5F7', color: period === p ? 'white' : '#5C636E', fontWeight: '600', cursor: 'pointer', fontSize: '0.75rem' }}>
                    {p === 'week' ? 'Sem.' : p === 'month' ? 'Mois' : 'An'}
                  </button>
                ))}
              </div>
            </div>
            {loadingTx ? (
              <div style={{ textAlign: 'center', padding: '1.5rem' }}><Loader size={24} className="spinning" style={{ color: '#FF7800' }} /></div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9AA0A8' }}>
                <TrendingUp size={32} style={{ marginBottom: '8px', opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Aucune transaction</p>
              </div>
            ) : (
              transactions.map((tx, i) => {
                const info = txTypeInfo(tx.type);
                const isCredit = tx.type === 'topup' || tx.type === 'refund';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < transactions.length - 1 ? '1px solid #F4F5F7' : 'none' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `${info.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {info.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem' }}>{info.label}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#9AA0A8' }}>{tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                    </div>
                    <p style={{ margin: 0, fontWeight: '700', color: isCredit ? '#10b981' : '#DC2626', fontSize: '0.95rem', flexShrink: 0 }}>
                      {isCredit ? '+' : '-'}{formatAmount(tx.amount)} FC
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // PAGE INBOX (MESSAGES / NOTIFICATIONS)
  // ============================================================
  const InboxPage = () => {
    const [activeFilter, setActiveFilter] = useState('all');
    const [notifications, setNotifications] = useState(clientNotifications);
    const [loading, setLoading] = useState(false);

    const ADMIN_TYPES = ['admin', 'admin_message', 'approval', 'warning', 'announcement'];
    const SYSTEM_TYPES = ['system', 'info', 'payment_received', 'subscription_created', 'subscription_expired', 'ride_update'];

    useEffect(() => {
      if (isAuthenticated) {
        setLoading(true);
        apiService.get('/users/me/notifications?limit=50')
          .then(res => {
            if (res.success) {
              setNotifications(res.notifications || []);
              setClientNotifications(res.notifications || []);
              setClientUnreadCount(res.unreadCount || 0);
            }
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markOneRead = async (notifId) => {
      try {
        await apiService.patch(`/users/me/notifications/${notifId}/read`);
        setNotifications(prev => prev.map(n => n.notification_id === notifId || n.id === notifId ? { ...n, read: true } : n));
        setClientUnreadCount(prev => Math.max(0, prev - 1));
      } catch (_) {}
    };

    const markAllRead = async () => {
      try {
        await apiService.patch('/users/me/notifications/mark-all-read');
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setClientUnreadCount(0);
      } catch (_) {}
    };

    const filtered = (() => {
      switch(activeFilter) {
        case 'unread': return notifications.filter(n => !n.read);
        case 'admin': return notifications.filter(n => ADMIN_TYPES.includes(n.type));
        case 'system': return notifications.filter(n => SYSTEM_TYPES.includes(n.type));
        default: return notifications;
      }
    })();

    const unreadCount = notifications.filter(n => !n.read).length;
    const adminCount = notifications.filter(n => ADMIN_TYPES.includes(n.type)).length;
    const systemCount = notifications.filter(n => SYSTEM_TYPES.includes(n.type)).length;

    const getNotifIcon = (type) => {
      switch(type) {
        case 'admin': case 'admin_message': case 'announcement': return <ShieldCheck size={20} color="#FF9B00" />;
        case 'approval': return <CheckCircle size={20} color="#10b981" />;
        case 'warning': return <AlertCircle size={20} color="#FFB900" />;
        case 'payment_received': case 'topup': return <Wallet size={20} color="#10b981" />;
        case 'subscription_created': case 'subscription_expired': return <CreditCard size={20} color="#FF7800" />;
        case 'ride_update': return <Car size={20} color="#FFB900" />;
        case 'system': case 'info': return <Info size={20} color="#5C636E" />;
        default: return <Bell size={20} color="#5C636E" />;
      }
    };

    const timeAgo = (dateStr) => {
      const d = new Date(dateStr);
      const diff = Math.floor((Date.now() - d) / 60000);
      if (diff < 1) return "À l'instant";
      if (diff < 60) return `Il y a ${diff} min`;
      if (diff < 1440) return `Il y a ${Math.floor(diff/60)}h`;
      return `Il y a ${Math.floor(diff/1440)}j`;
    };

    const tabs = [
      { id: 'all', label: 'Tout', count: notifications.length },
      { id: 'unread', label: 'Non lu', count: unreadCount },
      { id: 'admin', label: 'Admin', count: adminCount },
      { id: 'system', label: 'Système', count: systemCount },
    ];

    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF' }}>
        <div style={{ background: 'white', borderBottom: '1px solid #F4F5F7', padding: '1rem 1rem 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => setCurrentPage('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#414852', padding: 0 }}>
                <ArrowLeft size={22} />
              </button>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Messages</h2>
              {unreadCount > 0 && <span style={{ background: '#DC2626', color: 'white', borderRadius: '20px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: '700' }}>{unreadCount}</span>}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#FF7800', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
                Tout lire
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveFilter(tab.id)} style={{ padding: '10px 14px', border: 'none', background: 'none', borderBottom: activeFilter === tab.id ? '2px solid #FF7800' : '2px solid transparent', color: activeFilter === tab.id ? '#FF7800' : '#5C636E', fontWeight: activeFilter === tab.id ? '700' : '500', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {tab.label}
                {tab.count > 0 && <span style={{ background: activeFilter === tab.id ? '#FF7800' : '#E9EBEF', color: activeFilter === tab.id ? 'white' : '#5C636E', borderRadius: '10px', padding: '1px 6px', fontSize: '0.7rem', fontWeight: '700' }}>{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '0.5rem 0' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}><Loader size={32} className="spinning" style={{ color: '#FF7800' }} /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9AA0A8' }}>
              <Inbox size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
              <p style={{ margin: 0 }}>Aucun message</p>
            </div>
          ) : (
            filtered.map((notif) => {
              const nid = notif.notification_id || notif.id;
              return (
                <div
                  key={nid}
                  onClick={() => !notif.read && markOneRead(nid)}
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', background: notif.read ? 'white' : '#FEE9E2', borderBottom: '1px solid #F4F5F7', cursor: notif.read ? 'default' : 'pointer', transition: 'background 0.2s' }}
                >
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                    {getNotifIcon(notif.type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {notif.title && <p style={{ margin: '0 0 2px', fontWeight: notif.read ? '500' : '700', fontSize: '0.9rem', color: '#1C1C1C' }}>{notif.title}</p>}
                    <p style={{ margin: '0 0 4px', fontSize: '0.85rem', color: notif.read ? '#5C636E' : '#414852', lineHeight: 1.4 }}>{notif.message}</p>
                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#9AA0A8' }}>{timeAgo(notif.created_at)}</p>
                  </div>
                  {!notif.read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF7800', marginTop: '6px', flexShrink: 0 }} />}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ============================================================
  // PAGE LIVRAISON
  // ============================================================
  // Mbiyo Market — module livraison complet (src/market), pleine page.
  const DeliveryHomePage = () => (
    <MarketApp currentUser={user} isAuthenticated={isAuthenticated} citySlug={user?.city_slug || null} />
  );

  // ============================================================
  // PAGE ACCUEIL
  // ============================================================
  const HomePage = () => {
    const switchClientMode = (mode) => {
      setActiveClientMode(mode);
      localStorage.setItem('clientActiveMode', mode);
    };

    // Si mode livraison, afficher DeliveryHomePage
    if (activeClientMode === 'livraison') {
      return (
        <div>
          {/* Switcher mode */}
          <div style={{ display: 'flex', background: '#E9EBEF', borderRadius: '14px', padding: '4px', margin: '1rem 1rem 0', gap: '4px' }}>
            <button onClick={() => switchClientMode('transport')} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: 'white', color: '#5C636E', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Car size={16} /> Mbiyo
            </button>
            <button style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: '#059669', color: 'white', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Truck size={16} /> Livraison
            </button>
          </div>
          <Stable render={DeliveryHomePage} key="DeliveryHomePage" />
        </div>
      );
    }

    return (
    <div className="home-page">
      {/* Switcher mode Mbiyo / Livraison */}
      <div style={{ display: 'flex', background: '#E9EBEF', borderRadius: '14px', padding: '4px', marginBottom: '1rem', gap: '4px' }}>
        <button style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: '#F54119', color: 'white', fontWeight: '700', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Car size={16} /> Mbiyo
        </button>
        <button onClick={() => switchClientMode('livraison')} style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '10px', background: 'transparent', color: '#5C636E', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Truck size={16} /> Livraison
        </button>
      </div>

      <div className="hero-section">
        <div style={{display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem'}}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: user.photo ? 'transparent' : user.planColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            border: '3px solid rgba(255,255,255,0.3)',
            flexShrink: 0
          }}>
            {user.photo ? (
              <img src={user.photo} alt="Profil" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            ) : (
              <span style={{fontSize: '2rem'}}>👤</span>
            )}
          </div>
          <div>
            <h1>{t.welcome}, {user.name.split(' ')[0]} 👋</h1>
            <p className="subtitle">Votre mobilité simplifiée</p>
          </div>
        </div>
      </div>

      <div className="quick-qr">
        <div className="mini-qr">
          <QrCode size={80} strokeWidth={1.5} color={user.planColor} />
        </div>
        <div className="quick-qr-info">
          <h3>{t.scanToRide}</h3>
          <p className="plan-name" style={{color: user.planColor, fontWeight: '600'}}>🎫 {user.plan}</p>
          {activeSubscription && <p style={{fontSize: '0.85rem', color: '#666'}}>Actif jusqu'au {user.expiryDate}</p>}
          {!activeSubscription && <p style={{fontSize: '0.85rem', color: '#e74c3c'}}>❌ Aucun abonnement</p>}
          <button className="btn-primary" onClick={() => setCurrentPage('qr')} style={{background: user.planColor}}>
            {t.myQR}
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <Calendar size={24} />
          <div>
            <p className="stat-value">{user.daysLeft}</p>
            <p className="stat-label">{t.daysLeft}</p>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '8px' }}>
              <button
                onClick={() => setScanStatsFilter('today')}
                style={{
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: scanStatsFilter === 'today' ? '#F54119' : '#E9EBEF',
                  color: scanStatsFilter === 'today' ? 'white' : '#5C636E',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '11px',
                  transition: 'all 0.2s'
                }}
              >
                Auj.
              </button>
              <button
                onClick={() => setScanStatsFilter('week')}
                style={{
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: scanStatsFilter === 'week' ? '#F54119' : '#E9EBEF',
                  color: scanStatsFilter === 'week' ? 'white' : '#5C636E',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '11px',
                  transition: 'all 0.2s'
                }}
              >
                Sem.
              </button>
              <button
                onClick={() => setScanStatsFilter('month')}
                style={{
                  padding: '6px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: scanStatsFilter === 'month' ? '#F54119' : '#E9EBEF',
                  color: scanStatsFilter === 'month' ? 'white' : '#5C636E',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '11px',
                  transition: 'all 0.2s'
                }}
              >
                Mois
              </button>
            </div>
          </div>
          <MapPin size={24} />
          <div>
            <p className="stat-value">
              {scanStatsFilter === 'today' ? userScanStats.today : scanStatsFilter === 'week' ? userScanStats.week : userScanStats.month}
            </p>
            <p className="stat-label">
              {scanStatsFilter === 'today' ? 'Trajets auj.' : scanStatsFilter === 'week' ? 'Trajets cette sem.' : 'Trajets ce mois'}
            </p>
          </div>
        </div>
      </div>

      {/* Bouton Commander un Taxi */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={() => setCurrentPage('taxi-order')}
          style={{
            width: '100%',
            padding: '18px',
            background: 'linear-gradient(135deg, #FFB900, #FF9B00)',
            color: 'white',
            border: 'none',
            borderRadius: '16px',
            fontSize: '1.1rem',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: '0 4px 12px rgba(245,158,11,0.3)'
          }}
        >
          <Car size={24} /> Commander un Taxi
        </button>
      </div>
    </div>
    );
  };

  // ============================================================
  // PAGE SUPPORT / AIDE — 3 sections : Client · Chauffeur · Vendeur
  // ============================================================
  const SupportPage = () => {
    // Données des 3 apps natives
    const APPS = [
      {
        id: 'client',
        label: 'Client',
        emoji: '👤',
        color: '#FF7800',
        colorLight: '#FEE9E2',
        appType: 'Client (Passager)',
        faq: [
          { q: 'Comment commander une course ?', a: "Allez dans \"Commander un Taxi\", entrez votre départ et destination, choisissez le type de course, puis confirmez. Un chauffeur est assigné automatiquement." },
          { q: 'Comment annuler une course ?', a: "Vous pouvez annuler gratuitement dans les 5 premières minutes. Après ce délai, des frais d'annulation s'appliquent (500 CDF entre 5 et 10 min, puis 1 000 CDF + 200 CDF/min au-delà)." },
          { q: 'Mon paiement Mobile Money a échoué, que faire ?', a: "Vérifiez que votre solde est suffisant et que le numéro correspond à votre compte opérateur. Si le problème persiste, essayez un autre opérateur ou contactez votre réseau mobile." },
          { q: 'Comment recharger mon solde ?', a: "Menu → Mon Solde → \"Recharger mon solde\". Choisissez un montant, sélectionnez l'opérateur Mobile Money et confirmez sur votre téléphone." },
          { q: 'Comment mettre à jour mon abonnement ?', a: "Menu → Abonnement. Choisissez votre plan et payez via Mobile Money. L'abonnement est activé dès confirmation du paiement." },
          { q: "Que faire si mon chauffeur n'arrive pas ?", a: "Si le chauffeur tarde trop, annulez la course et commandez-en une nouvelle. Signalez le problème via le formulaire ci-dessous." },
          { q: 'Comment vérifier mon identité ?', a: "Menu → Mon Profil → onglet \"Identité\". Uploadez un document officiel (CNI, passeport ou permis) et prenez un selfie. Validation sous 24h." },
          { q: 'Comment utiliser mon QR Code ?', a: "Votre QR Code est lié à votre abonnement actif. Présentez-le au chauffeur qui le scannera pour valider votre trajet. Accessible depuis l'accueil ou Menu → Mon QR Code." },
        ],
      },
      {
        id: 'chauffeur',
        label: 'Chauffeur',
        emoji: '🚗',
        color: '#10b981',
        colorLight: '#d1fae5',
        appType: 'Chauffeur',
        faq: [
          { q: 'Comment activer ma disponibilité ?', a: "Sur votre tableau de bord, utilisez le toggle \"En ligne / Hors ligne\". Passez en mode \"En ligne\" pour recevoir des demandes de course. Pensez à le désactiver en fin de journée." },
          { q: 'Comment fonctionne le scanner QR ?', a: "Allez dans l'onglet \"Scanner\" et pointez la caméra vers le QR code du passager. La validation fonctionne aussi hors connexion — les scans sont synchronisés automatiquement dès le retour sur internet." },
          { q: 'Comment sont calculées mes commissions ?', a: "La commission dépend du type de course : 20% pour Moto et Covoiturage, 25% pour Mbiyo et Confort, 30% pour Premium. Elle est prélevée automatiquement sur chaque course." },
          { q: 'Pourquoi mon compte est-il bloqué ?', a: "Un compte est bloqué en cas de dette envers la plateforme. Rechargez votre balance via l'onglet \"Solde\" pour le débloquer automatiquement." },
          { q: 'Comment recharger ma balance ?', a: "Onglet \"Solde\" → \"Recharger\". Choisissez un montant (min 1 000 CDF), sélectionnez votre opérateur et confirmez sur votre téléphone. Des frais de service de 3% s'appliquent." },
          { q: 'Comment fonctionne le paiement Mobile Money en course ?', a: "Le passager reçoit une notification pour payer via son opérateur pendant la course. Vous êtes alerté si le paiement n'a pas été effectué. En cas de non-paiement, collectez le montant en espèces." },
        ],
      },
      {
        id: 'vendeur',
        label: 'Vendeur',
        emoji: '🏪',
        color: '#FFB900',
        colorLight: '#fef3c7',
        appType: 'Fournisseur',
        faq: [
          { q: 'Comment ajouter un produit ?', a: "Allez dans l'onglet \"Produits\" et appuyez sur le bouton +. Remplissez le nom, la description, le prix et uploadez une photo. Activez la disponibilité pour que le produit soit visible par les clients." },
          { q: 'Comment activer ou fermer mon commerce ?', a: "Sur votre tableau de bord, utilisez le toggle \"Ouvert / Fermé\". Quand votre commerce est fermé, les clients ne peuvent pas passer commande." },
          { q: 'Comment mettre un produit en promotion ?', a: "Dans la fiche d'un produit, activez le toggle \"Prix promotionnel\", saisissez le prix réduit et définissez les dates de début et de fin. Le badge PROMO s'affiche automatiquement." },
          { q: 'Comment voir et gérer mes commandes ?', a: "Allez dans l'onglet \"Commandes\". Filtrez par statut et cliquez sur une commande pour voir les détails, changer le statut ou contacter le client." },
          { q: 'Comment fonctionne la commission ?', a: "La plateforme prélève 10% sur chaque vente. Pour les paiements Mobile Money, le revenu net vous est crédité. Pour les paiements cash, seule la commission est débitée de votre balance." },
          { q: 'Comment recharger ma balance ?', a: "Mon Compte → Solde → \"Recharger\". Choisissez un montant, sélectionnez votre opérateur Mobile Money et confirmez. Des frais de service de 3% s'appliquent." },
        ],
      },
    ];

    const [activeApp, setActiveApp] = useState('client');
    const [openFaq, setOpenFaq] = useState(null);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    const app = APPS.find(a => a.id === activeApp);

    // Réinitialiser FAQ et formulaire au changement d'onglet
    const switchApp = (id) => {
      setActiveApp(id);
      setOpenFaq(null);
      setMessage('');
      setSendResult(null);
    };

    const handleSend = async () => {
      if (!message.trim() || message.trim().length < 10) return;
      setSending(true);
      setSendResult(null);
      try {
        const res = await apiService.post('/support/report', {
          message: message.trim(),
          app_type: app.appType,
          user_name: currentUser?.name || 'Anonyme',
          user_id: currentUser?.user_id || '—',
        });
        if (res.success) {
          setSendResult('success');
          setMessage('');
        } else {
          setSendResult('error');
        }
      } catch {
        setSendResult('error');
      } finally {
        setSending(false);
      }
    };

    const goBack = () => setCurrentPage(isAuthenticated ? 'home' : 'landing');

    const cardStyle = { background: 'white', borderRadius: '16px', padding: '16px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' };

    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF' }}>

        {/* Header sticky */}
        <div style={{ background: 'white', borderBottom: '1px solid #F4F5F7', padding: '0.9rem 1rem 0', position: 'sticky', top: 0, zIndex: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#12151A', padding: '4px', display: 'flex' }}>
              <ArrowLeft size={22} />
            </button>
            <h1 style={{ flex: 1, textAlign: 'center', margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#12151A' }}>Aide & Support</h1>
            <div style={{ width: 30 }} />
          </div>

          {/* Onglets */}
          <div style={{ display: 'flex' }}>
            {APPS.map(a => (
              <button
                key={a.id}
                onClick={() => switchApp(a.id)}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeApp === a.id ? `3px solid ${a.color}` : '3px solid transparent',
                  color: activeApp === a.id ? a.color : '#5C636E',
                  fontWeight: activeApp === a.id ? '700' : '500',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '3px',
                  transition: 'color 0.15s',
                }}
              >
                <span style={{ fontSize: '1.3rem' }}>{a.emoji}</span>
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bannière colorée */}
        <div style={{ background: `linear-gradient(135deg, ${app.color}, ${app.color}cc)`, padding: '1.25rem 1rem', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>
            {app.emoji}
          </div>
          <div>
            <p style={{ margin: '0 0 2px', fontSize: '0.75rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Support</p>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800' }}>App {app.label}</h2>
            <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.85 }}>{app.faq.length} questions fréquentes</p>
          </div>
        </div>

        <div style={{ padding: '1rem', maxWidth: '600px', margin: '0 auto' }}>

          {/* FAQ */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <HelpCircle size={20} color={app.color} />
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#12151A' }}>Questions fréquentes</h2>
            </div>
            {app.faq.map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '13px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', textAlign: 'left' }}
                >
                  <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: '600', color: '#1C1C1C', lineHeight: 1.4 }}>{item.q}</span>
                  <ChevronDown
                    size={17}
                    color={app.color}
                    style={{ flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === idx ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                {openFaq === idx && (
                  <div style={{ background: app.colorLight, borderRadius: '10px', padding: '12px 14px', marginBottom: '10px' }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#414852', lineHeight: 1.65 }}>{item.a}</p>
                  </div>
                )}
                {idx < app.faq.length - 1 && <div style={{ height: '1px', background: '#F4F5F7' }} />}
              </div>
            ))}
          </div>

          {/* Formulaire signalement */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <AlertCircle size={20} color="#FFB900" />
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#12151A' }}>Signaler un problème</h2>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: '#5C636E' }}>
              Ce message sera envoyé au support <strong>App {app.label}</strong>.
            </p>

            {sendResult === 'success' && (
              <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={18} color="#059669" />
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#065f46', fontWeight: '600' }}>Message envoyé ! Notre équipe vous répondra rapidement.</p>
              </div>
            )}
            {sendResult === 'error' && (
              <div style={{ background: '#fee2e2', border: '1px solid #FEE2E2', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <XCircle size={18} color="#dc2626" />
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#991b1b', fontWeight: '600' }}>Erreur lors de l'envoi. Veuillez réessayer.</p>
              </div>
            )}

            <textarea
              value={message}
              onChange={e => setMessage(e.target.value.slice(0, 500))}
              placeholder={`Décrivez votre problème sur l'app ${app.label} en détail...`}
              rows={4}
              style={{ width: '100%', padding: '14px', border: `1px solid #E9EBEF`, borderRadius: '12px', fontSize: '0.875rem', color: '#1C1C1C', resize: 'vertical', minHeight: '100px', background: '#FFFFFF', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
            />
            <p style={{ textAlign: 'right', fontSize: '0.72rem', color: '#9AA0A8', margin: '4px 0 12px' }}>{message.length}/500</p>
            <button
              onClick={handleSend}
              disabled={message.trim().length < 10 || sending}
              style={{ width: '100%', padding: '14px', background: app.color, color: 'white', border: 'none', borderRadius: '12px', fontSize: '0.95rem', fontWeight: '700', cursor: message.trim().length < 10 || sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: message.trim().length < 10 || sending ? 0.5 : 1, transition: 'opacity 0.2s' }}
            >
              {sending ? <Loader size={18} className="spinning" /> : <Send size={18} />}
              {sending ? 'Envoi en cours...' : 'Envoyer'}
            </button>
          </div>

          {/* Contact direct (partagé) */}
          <div style={{ ...cardStyle, marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <MessageCircle size={20} color="#FF9B00" />
              <h2 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#12151A' }}>Contact direct</h2>
            </div>
            <a href="mailto:support@taxicenterapp.com" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#FFFFFF', borderRadius: '10px', textDecoration: 'none' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#FEE9E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Mail size={18} color="#FF7800" />
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '0.875rem', color: '#1C1C1C' }}>Envoyer un email</p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#5C636E' }}>support@taxicenterapp.com</p>
              </div>
              <ChevronRight size={16} color="#9AA0A8" style={{ marginLeft: 'auto' }} />
            </a>
          </div>

        </div>
      </div>
    );
  };

  // ==============================================
  // TAXI ORDER PAGE — UBER-STYLE
  // ==============================================
  // Memoize pour garder une identité stable et éviter la perte de state lors des re-renders parent (polling notifications)
  const TaxiOrderPage = useMemo(() => function TaxiOrderPageComponent() {
    // Steps: map (address+map select), price (confirm+pay), searching, active, completed, history
    const [taxiStep, setTaxiStep] = useState('map');
    
    // Coordinates & addresses
    const [pickupCoords, setPickupCoords] = useState(null); // {lat, lng}
    const [dropoffCoords, setDropoffCoords] = useState(null);
    const [pickupAddress, setPickupAddress] = useState('');
    const [dropoffAddress, setDropoffAddress] = useState('');
    const [pickupSearch, setPickupSearch] = useState('');
    const [dropoffSearch, setDropoffSearch] = useState('');
    const [pickupSuggestions, setPickupSuggestions] = useState([]);
    const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
    const [activeInput, setActiveInput] = useState('pickup'); // which input is focused
    const [routeCoords, setRouteCoords] = useState([]); // OSRM route polyline
    const [distanceKm, setDistanceKm] = useState(null);
    const [locatingUser, setLocatingUser] = useState(false);
    const [gpsAccuracy, setGpsAccuracy] = useState(null); // GPS accuracy in meters

    // Pricing & payment
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [priceEstimate, setPriceEstimate] = useState(null);
    const [loadingEstimate, setLoadingEstimate] = useState(false);
    const [loadingRequest, setLoadingRequest] = useState(false);
    const [rideType, setRideType] = useState(null); // covoiturage|mbiyo|confort|premium — null = pas encore choisi
    const [allPrices, setAllPrices] = useState(null); // { covoiturage: {...}, mbiyo: {...}, confort: {...}, premium: {...} }
    const [subscriberDiscountInfo, setSubscriberDiscountInfo] = useState({ rate: 0, plan: null });

    // Active ride
    const [activeRide, setActiveRide] = useState(null);
    const [rideHistory, setRideHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatText, setChatText] = useState('');
    const [showChat, setShowChat] = useState(false);
    const chatMessagesCountRef = useRef(0);
    const [chatNotifBadge, setChatNotifBadge] = useState(false);
    const [showTaxiPaymentModal, setShowTaxiPaymentModal] = useState(false);
    const [taxiPaymentCompleted, setTaxiPaymentCompleted] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelEstimate, setCancelEstimate] = useState(null);
    const [loadingCancelEstimate, setLoadingCancelEstimate] = useState(false);
    const [ratingValue, setRatingValue] = useState(0);
    const [ratingComment, setRatingComment] = useState('');
    const [ratingTags, setRatingTags] = useState([]);
    const [ratingCriteria, setRatingCriteria] = useState(null);
    const [taxiError, setTaxiError] = useState('');
    const [searchTimer, setSearchTimer] = useState(300);
    const searchIntervalRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const mapRef = useRef(null);
    const previousStatusRef = useRef(null);
    const [driverArrivedAlert, setDriverArrivedAlert] = useState(false);
    const audioCtxRef = useRef(null);
    const [driverLocation, setDriverLocation] = useState(null);
    const [driverRoute, setDriverRoute] = useState(null);
    const clientMapRef = useRef(null);

    // Bukavu center
    const BUKAVU_CENTER = { lat: -2.5083, lng: 28.8608 };

    const statusLabels = {
      searching: '🔍 Recherche d\'un chauffeur...',
      accepted: '✅ Chauffeur trouvé !',
      driver_arriving: '📍 Chauffeur arrivé au point de prise en charge',
      in_progress: '🚗 Course en cours',
      completed: '🎉 Course terminée',
      cancelled_client: '❌ Annulée par vous',
      cancelled_driver: '❌ Annulée par le chauffeur',
      no_driver_found: '😞 Aucun chauffeur disponible'
    };

    // ——————————————————————————
    // GOOGLE MAPS — Load API
    // ——————————————————————————
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      libraries: GOOGLE_MAPS_LIBRARIES,
    });

    // ——————————————————————————
    // GOOGLE MAPS — Geocoding & Places
    // ——————————————————————————
    const autocompleteServiceRef = useRef(null);
    const geocoderRef = useRef(null);
    const directionsServiceRef = useRef(null);

    // Initialize Google Services when map loads
    const onMapLoad = useCallback((map) => {
      mapRef.current = map;
      if (window.google) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
    }, []);

    const searchAddress = async (query, type) => {
      if (query.length < 1 || !autocompleteServiceRef.current) {
        type === 'pickup' ? setPickupSuggestions([]) : setDropoffSuggestions([]);
        return;
      }
      try {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: query,
            locationBias: { center: { lat: BUKAVU_CENTER.lat, lng: BUKAVU_CENTER.lng }, radius: 30000 },
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
              const suggestions = predictions.map(p => ({
                mainText: p.structured_formatting?.main_text || p.description.split(',')[0],
                secondaryText: p.structured_formatting?.secondary_text || p.description.split(',').slice(1).join(',').trim(),
                placeId: p.place_id,
              }));
              type === 'pickup' ? setPickupSuggestions(suggestions) : setDropoffSuggestions(suggestions);
            } else {
              type === 'pickup' ? setPickupSuggestions([]) : setDropoffSuggestions([]);
            }
          }
        );
      } catch (err) {
        console.error('Places API error:', err);
        type === 'pickup' ? setPickupSuggestions([]) : setDropoffSuggestions([]);
      }
    };

    // Resolve a place_id to coordinates
    const resolvePlace = (placeId) => {
      return new Promise((resolve, reject) => {
        if (!geocoderRef.current) return reject('Geocoder not ready');
        geocoderRef.current.geocode({ placeId }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng(), address: results[0].formatted_address?.split(',').slice(0, 3).join(', ') });
          } else {
            reject('Place not found');
          }
        });
      });
    };

    // Reverse geocode
    const reverseGeocode = async (lat, lng) => {
      return new Promise((resolve) => {
        if (!geocoderRef.current) {
          resolve(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          return;
        }
        geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0].formatted_address?.split(',').slice(0, 3).join(', ') || `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          } else {
            resolve(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
          }
        });
      });
    };

    // Debounced search
    const handleSearchInput = (text, type) => {
      if (type === 'pickup') {
        setPickupSearch(text);
        // Réinitialiser les coordonnées pickup pour permettre un nouveau calcul
        if (pickupCoords) {
          setPickupCoords(null);
          setPickupAddress('');
        }
      } else {
        setDropoffSearch(text);
        // Réinitialiser les coordonnées dropoff pour permettre un nouveau calcul
        if (dropoffCoords) {
          setDropoffCoords(null);
          setDropoffAddress('');
        }
      }
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      // Shorter debounce for better responsiveness
      searchTimeoutRef.current = setTimeout(() => searchAddress(text, type), 150);
    };

    // Show default suggestions when focusing an empty field
    const handleInputFocus = (type) => {
      setActiveInput(type);
    };

    // Select suggestion — always via Google place_id
    const selectSuggestion = async (suggestion, type) => {
      try {
        const result = await resolvePlace(suggestion.placeId);
        // Show place name + address (e.g. "Église Terre Sainte, Avenue Lumumba, Bukavu")
        const displayText = suggestion.mainText
          ? `${suggestion.mainText}, ${result.address}`
          : result.address;
        if (type === 'pickup') {
          setPickupCoords({ lat: result.lat, lng: result.lng });
          setPickupAddress(displayText);
          setPickupSearch(displayText);
          setPickupSuggestions([]);
          setActiveInput('dropoff');
        } else {
          setDropoffCoords({ lat: result.lat, lng: result.lng });
          setDropoffAddress(displayText);
          setDropoffSearch(displayText);
          setDropoffSuggestions([]);
          setActiveInput(null);
        }
      } catch (err) {
        console.error('Place resolve error:', err);
      }
    };

    // ——————————————————————————
    // GEOLOCATION — current position
    // ——————————————————————————
    const useCurrentLocation = () => {
      if (!navigator.geolocation) {
        setTaxiError('La géolocalisation n\'est pas supportée');
        return;
      }
      setLocatingUser(true);
      setTaxiError('');

      let bestPosition = null;
      let attempts = 0;
      const MAX_ATTEMPTS = 6;
      const GOOD_ACCURACY = 50;

      const applyPosition = async (latitude, longitude, accuracy) => {
        setPickupCoords({ lat: latitude, lng: longitude });
        setGpsAccuracy(Math.round(accuracy));
        const addr = await reverseGeocode(latitude, longitude);
        setPickupAddress(addr);
        setPickupSearch(addr);
        setPickupSuggestions([]);
        setLocatingUser(false);
        setActiveInput('dropoff');
        console.log(`📍 Position: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} (±${Math.round(accuracy)}m)`);
      };

      const watchId = navigator.geolocation.watchPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          attempts++;
          if (!bestPosition || accuracy < bestPosition.accuracy) {
            bestPosition = { latitude, longitude, accuracy };
          }
          if (accuracy <= GOOD_ACCURACY) {
            navigator.geolocation.clearWatch(watchId);
            await applyPosition(latitude, longitude, accuracy);
            return;
          }
          if (attempts >= MAX_ATTEMPTS) {
            navigator.geolocation.clearWatch(watchId);
            if (bestPosition) {
              await applyPosition(bestPosition.latitude, bestPosition.longitude, bestPosition.accuracy);
              if (bestPosition.accuracy > 200) {
                setTaxiError(`Position approximative (~${Math.round(bestPosition.accuracy)}m). Glissez le marqueur pour ajuster.`);
              }
            }
          }
        },
        (err) => {
          navigator.geolocation.clearWatch(watchId);
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              await applyPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
            },
            () => {
              setTaxiError('Impossible d\'obtenir votre position.');
              setLocatingUser(false);
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
          );
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
      );

      setTimeout(() => {
        navigator.geolocation.clearWatch(watchId);
        if (bestPosition && !bestPosition.applied) {
          bestPosition.applied = true;
          applyPosition(bestPosition.latitude, bestPosition.longitude, bestPosition.accuracy);
        }
      }, 12000);
    };

    // ——————————————————————————
    // GEOCODE & CALCULATE — resolve text to coords
    // ——————————————————————————
    const [geocoding, setGeocoding] = useState(false);

    const geocodeAndCalculate = async () => {
      setGeocoding(true);
      setTaxiError(null);
      try {
        let pCoords = pickupCoords;
        let dCoords = dropoffCoords;

        if (!pCoords && pickupSearch.trim() && geocoderRef.current) {
          const result = await new Promise((resolve, reject) => {
            geocoderRef.current.geocode(
              { address: pickupSearch.trim() + ', Bukavu, DR Congo', componentRestrictions: { country: 'cd' } },
              (results, status) => {
                if (status === 'OK' && results[0]) {
                  const loc = results[0].geometry.location;
                  resolve({ lat: loc.lat(), lng: loc.lng(), addr: results[0].formatted_address?.split(',').slice(0, 3).join(', ') });
                } else reject('not found');
              }
            );
          });
          pCoords = { lat: result.lat, lng: result.lng };
          setPickupCoords(pCoords);
          setPickupAddress(result.addr);
        }

        if (!dCoords && dropoffSearch.trim() && geocoderRef.current) {
          const result = await new Promise((resolve, reject) => {
            geocoderRef.current.geocode(
              { address: dropoffSearch.trim() + ', Bukavu, DR Congo', componentRestrictions: { country: 'cd' } },
              (results, status) => {
                if (status === 'OK' && results[0]) {
                  const loc = results[0].geometry.location;
                  resolve({ lat: loc.lat(), lng: loc.lng(), addr: results[0].formatted_address?.split(',').slice(0, 3).join(', ') });
                } else reject('not found');
              }
            );
          });
          dCoords = { lat: result.lat, lng: result.lng };
          setDropoffCoords(dCoords);
          setDropoffAddress(result.addr);
        }

        if (!pCoords || !dCoords) {
          setTaxiError('Veuillez entrer les deux adresses.');
          setGeocoding(false);
          return;
        }
        setActiveInput(null);
      } catch (err) {
        setTaxiError('Adresse introuvable. Précisez ou utilisez la carte.');
      }
      setGeocoding(false);
    };

    // ——————————————————————————
    // GOOGLE DIRECTIONS — distance + route
    // ——————————————————————————
    const [directionsResult, setDirectionsResult] = useState(null);

    const calculateRoute = async (pickup, dropoff) => {
      // Try Google Directions first
      if (directionsServiceRef.current) {
        try {
          const result = await new Promise((resolve, reject) => {
            directionsServiceRef.current.route(
              {
                origin: { lat: pickup.lat, lng: pickup.lng },
                destination: { lat: dropoff.lat, lng: dropoff.lng },
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === 'OK') resolve(result);
                else reject(status);
              }
            );
          });
          setDirectionsResult(result);
          const leg = result.routes[0].legs[0];
          const km = leg.distance.value / 1000;
          setDistanceKm(Math.round(km * 10) / 10);
          setRouteCoords([]); // Not needed — DirectionsRenderer draws the route
          return km;
        } catch (err) {
          console.warn('Google Directions failed, using Haversine fallback:', err);
        }
      }

      // Fallback: Haversine * 1.3
      const R = 6371;
      const dLat = (dropoff.lat - pickup.lat) * Math.PI / 180;
      const dLng = (dropoff.lng - pickup.lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(pickup.lat * Math.PI / 180) * Math.cos(dropoff.lat * Math.PI / 180)
        * Math.sin(dLng / 2) ** 2;
      const straight = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const km = straight * 1.3;
      setDistanceKm(Math.round(km * 10) / 10);
      setDirectionsResult(null);
      setRouteCoords([]);
      return km;
    };

    // ——————————————————————————
    // AUTO-CALCULATE ALL PRICES when both points set
    // ——————————————————————————
    useEffect(() => {
      if (pickupCoords && dropoffCoords) {
        (async () => {
          setLoadingEstimate(true);
          const km = await calculateRoute(pickupCoords, dropoffCoords);
          if (km) {
            try {
              const res = await apiService.post('/taxi/estimate', { distance_km: km, ride_type: 'all' });
              if (res.success && res.prices) {
                setAllPrices(res.prices);
                setSubscriberDiscountInfo({ rate: res.subscriber_discount_rate || 0, plan: res.subscriber_plan || null });
                // Si un type est déjà choisi, mettre à jour priceEstimate
                if (rideType && res.prices[rideType]) {
                  setPriceEstimate({ success: true, ...res.prices[rideType], has_subscriber_discount: res.has_subscriber_discount, subscriber_discount_rate: res.subscriber_discount_rate, subscriber_plan: res.subscriber_plan, is_night: res.is_night, currency: res.currency });
                }
              }
            } catch (err) {
              console.error('Estimate error:', err);
            }
          }
          setLoadingEstimate(false);
        })();
      } else {
        setPriceEstimate(null);
        setAllPrices(null);
        setRideType(null);
        setDistanceKm(null);
        setRouteCoords([]);
      }
    }, [pickupCoords, dropoffCoords]);

    // Quand l'utilisateur choisit un type, remplir priceEstimate
    useEffect(() => {
      if (rideType && allPrices && allPrices[rideType]) {
        setPriceEstimate({ success: true, ...allPrices[rideType], has_subscriber_discount: subscriberDiscountInfo.rate > 0, subscriber_discount_rate: subscriberDiscountInfo.rate, subscriber_plan: subscriberDiscountInfo.plan, currency: 'CDF' });
      } else if (!rideType) {
        setPriceEstimate(null);
      }
    }, [rideType, allPrices]);

    // ——————————————————————————
    // CHECK ACTIVE RIDE on mount
    // ——————————————————————————
    useEffect(() => {
      if (currentUserRef.current) checkActiveRide();
      return () => {
        if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      };
    }, []);

    const checkActiveRide = async () => {
      try {
        const res = await apiService.get(`/taxi/client/${currentUserRef.current.user_id}/active`);
        if (res.ride) {
          setActiveRide(res.ride);
          if (res.ride.status === 'searching') {
            setTaxiStep('searching');
            startSearchCountdown(res.ride);
          } else if (['accepted', 'driver_arriving', 'in_progress'].includes(res.ride.status)) {
            setTaxiStep('active');
            startPolling(res.ride.ride_id);
          } else if (res.ride.status === 'completed' && !res.ride.rating_by_client) {
            setTaxiStep('completed');
          }
        }
      } catch (err) {
        console.error('Erreur vérification course active:', err);
      }
    };

    // ——————————————————————————
    // REQUEST RIDE
    // ——————————————————————————
    const requestRide = async () => {
      setTaxiError('');
      if (!pickupCoords || !pickupAddress) return setTaxiError('Sélectionnez un lieu de départ');
      if (!dropoffCoords || !dropoffAddress) return setTaxiError('Sélectionnez une destination');
      if (!distanceKm) return setTaxiError('Calcul de distance en cours...');
      if (!rideType) return setTaxiError('Choisissez un type de course');

      setLoadingRequest(true);
      try {
        const res = await apiService.post('/taxi/request', {
          pickup_address: pickupAddress,
          pickup_lat: pickupCoords.lat,
          pickup_lng: pickupCoords.lng,
          dropoff_address: dropoffAddress,
          dropoff_lat: dropoffCoords.lat,
          dropoff_lng: dropoffCoords.lng,
          distance_km: distanceKm,
          payment_method: paymentMethod,
          ride_type: rideType
        });
        if (res.success) {
          const rideRes = await apiService.get(`/taxi/client/${currentUserRef.current.user_id}/active`);
          setActiveRide(rideRes.ride || { ride_id: res.ride_id, status: 'searching', expires_at: res.expires_at, estimated_price: res.estimated_price });
          setTaxiStep('searching');
          startSearchCountdown({ ride_id: res.ride_id, expires_at: res.expires_at });
        } else {
          setTaxiError(res.error || 'Erreur lors de la commande');
        }
      } catch (err) {
        setTaxiError(err.response?.data?.error || 'Erreur lors de la commande');
      }
      setLoadingRequest(false);
    };

    const startSearchCountdown = (ride) => {
      const expiresAt = new Date(ride.expires_at).getTime();
      if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);

      searchIntervalRef.current = setInterval(async () => {
        const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        setSearchTimer(remaining);

        try {
          const res = await apiService.get(`/taxi/client/${currentUserRef.current.user_id}/active`);
          // Ride expired or cleaned up on server — no longer active
          if (!res.ride) {
            clearInterval(searchIntervalRef.current);
            setTaxiStep('map');
            setActiveRide(null);
            setTaxiError('Aucun chauffeur disponible. Réessayez.');
            return;
          }
          if (res.ride.status !== 'searching') {
            clearInterval(searchIntervalRef.current);
            setActiveRide(res.ride);
            if (['accepted', 'driver_arriving', 'in_progress'].includes(res.ride.status)) {
              setTaxiStep('active');
              previousStatusRef.current = res.ride.status;
              startPolling(res.ride.ride_id);
              // Si le chauffeur est déjà arrivé
              if (res.ride.status === 'driver_arriving') {
                setDriverArrivedAlert(true);
                playClientNotifSound();
                if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
                setTimeout(() => setDriverArrivedAlert(false), 12000);
              }
            } else if (res.ride.status === 'completed') {
              setTaxiStep('completed');
            }
            return;
          }
        } catch (err) { /* continue */ }

        if (remaining <= 0) {
          clearInterval(searchIntervalRef.current);
          setTaxiStep('map');
          setActiveRide(null);
          setTaxiError('Aucun chauffeur disponible. Réessayez.');
        }
      }, 8000);
    };

    // Notification sonore pour le client
    const playClientNotifSound = useCallback(() => {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;
        [523, 659, 784, 1047].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.3, now + i * 0.2);
          gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.2 + 0.3);
          osc.start(now + i * 0.2);
          osc.stop(now + i * 0.2 + 0.3);
        });
      } catch (e) { /* audio not supported */ }
    }, []);

    const startPolling = (rideId) => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(async () => {
        try {
          const res = await apiService.get(`/taxi/${rideId}`);
          if (res.success) {
            // Détecter changement de statut vers driver_arriving
            const prevStatus = previousStatusRef.current;
            const newStatus = res.ride.status;
            if (newStatus === 'driver_arriving' && prevStatus !== 'driver_arriving') {
              // Chauffeur arrivé ! Alerte
              setDriverArrivedAlert(true);
              playClientNotifSound();
              if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
              setTimeout(() => setDriverArrivedAlert(false), 12000);
            }
            previousStatusRef.current = newStatus;

            // Mise à jour position chauffeur
            if (res.ride.driver_location?.lat && res.ride.driver_location?.lng) {
              setDriverLocation({ lat: res.ride.driver_location.lat, lng: res.ride.driver_location.lng });
            }

            setActiveRide(res.ride);
            if (res.ride.status === 'completed') {
              clearInterval(pollIntervalRef.current);
              setDriverLocation(null);
              setDriverRoute(null);
              // If mobile money, show payment modal first
              if (res.ride.payment_method === 'mobile_money' && res.ride.payment_status !== 'paid') {
                setShowTaxiPaymentModal(true);
              }
              setTaxiStep('completed');
            } else if (['cancelled_client', 'cancelled_driver', 'no_driver_found'].includes(res.ride.status)) {
              clearInterval(pollIntervalRef.current);
              setDriverLocation(null);
              setDriverRoute(null);
              setTaxiStep('map');
              setActiveRide(null);
              setTaxiError(statusLabels[res.ride.status]);
            }
          }
        } catch (err) { /* continue */ }
      }, 10000);
    };

    const cancelRide = async () => {
      if (!activeRide) return;
      // Always clear intervals and reset state
      if (searchIntervalRef.current) clearInterval(searchIntervalRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      try {
        await apiService.delete(`/taxi/${activeRide.ride_id}/cancel`);
      } catch (err) {
        console.warn('Cancel API error (ride may already be expired):', err.message);
      }
      // Reset to map regardless of API success — ride will expire on server anyway
      setActiveRide(null);
      setTaxiStep('map');
      setTaxiError('');
      setShowCancelConfirm(false);
      setCancelEstimate(null);
    };

    const requestCancelWithEstimate = async () => {
      if (!activeRide) return;
      try {
        setLoadingCancelEstimate(true);
        const res = await apiService.get(`/taxi/${activeRide.ride_id}/cancellation-estimate`);
        if (res.success) {
          setCancelEstimate(res);
          setShowCancelConfirm(true);
        } else {
          // Fallback: annuler sans estimation
          cancelRide();
        }
      } catch (err) {
        // Si l'API échoue, annuler directement
        cancelRide();
      } finally { setLoadingCancelEstimate(false); }
    };

    const sendMessage = async () => {
      if (!chatText.trim() || !activeRide) return;
      try {
        await apiService.post(`/taxi/${activeRide.ride_id}/messages`, { text: chatText });
        setChatText('');
        loadMessages();
      } catch (err) { /* ignore */ }
    };

    const loadMessages = async () => {
      if (!activeRide) return;
      try {
        const res = await apiService.get(`/taxi/${activeRide.ride_id}/messages`);
        if (res.success) {
          // Detect new messages from driver → notification
          const newCount = res.messages.length;
          if (newCount > chatMessagesCountRef.current && chatMessagesCountRef.current > 0) {
            const lastMsg = res.messages[res.messages.length - 1];
            if (lastMsg && lastMsg.sender_type === 'driver') {
              playClientNotifSound();
              if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
              if (!showChat) setChatNotifBadge(true);
            }
          }
          chatMessagesCountRef.current = newCount;
          setChatMessages(res.messages);
        }
      } catch (err) { /* ignore */ }
    };

    // Fin de course : remise à zéro de l'écran (après la note, ou « Plus tard »)
    const resetAfterRide = () => {
      setTaxiStep('map');
      setActiveRide(null);
      setPickupCoords(null);
      setDropoffCoords(null);
      setPickupAddress('');
      setDropoffAddress('');
      setPickupSearch('');
      setDropoffSearch('');
      setRatingValue(0);
      setRatingComment('');
      setRatingTags([]);
      setRatingCriteria(null);
    };

    const submitRating = async () => {
      if (!ratingValue || !activeRide) return;
      try {
        await apiService.post(`/taxi/${activeRide.ride_id}/rate/client`, {
          rating: ratingValue,
          tags: ratingTags,
          comment: ratingComment
        });
        setTaxiStep('map');
        setActiveRide(null);
        setPickupCoords(null);
        setDropoffCoords(null);
        setPickupAddress('');
        setDropoffAddress('');
        setPickupSearch('');
        setDropoffSearch('');
        setRatingValue(0);
        setRatingComment('');
        setRatingTags([]);
        setRatingCriteria(null);
      } catch (err) {
        setTaxiError('Erreur lors de la notation');
      }
    };

    // Charger les critères de notation quand la course est terminée
    useEffect(() => {
      if (taxiStep === 'completed' && !ratingCriteria) {
        apiService.get('/ratings/criteria?role=client')
          .then(res => { if (res.success) setRatingCriteria(res.criteria); })
          .catch(() => {});
      }
    }, [taxiStep, ratingCriteria]);

    const loadHistory = async () => {
      setLoadingHistory(true);
      try {
        const res = await apiService.get(`/taxi/client/${currentUserRef.current.user_id}/history`);
        if (res.success) setRideHistory(res.rides);
      } catch (err) { /* ignore */ }
      setLoadingHistory(false);
    };

    useEffect(() => {
      if (showChat) setChatNotifBadge(false);
    }, [showChat]);

    // Chat polling - poll even when closed for notifications
    useEffect(() => {
      if (activeRide) {
        loadMessages();
        const chatInterval = setInterval(loadMessages, 15000);
        return () => clearInterval(chatInterval);
      }
    }, [activeRide?.ride_id]);

    // Reset to new ride
    const resetToNewRide = () => {
      setTaxiStep('map');
      setActiveRide(null);
      setPickupCoords(null);
      setDropoffCoords(null);
      setPickupAddress('');
      setDropoffAddress('');
      setPickupSearch('');
      setDropoffSearch('');
      setPriceEstimate(null);
      setDistanceKm(null);
      setRouteCoords([]);
      setTaxiError('');
      setDriverArrivedAlert(false);
      previousStatusRef.current = null;
      setDriverLocation(null);
      setDriverRoute(null);
      setRideType(null);
      setAllPrices(null);
    };

    // ——————————————————————————
    // GOOGLE MAPS — Route chauffeur en temps réel
    // ——————————————————————————
    useEffect(() => {
      if (!driverLocation || !activeRide || !isLoaded || !window.google) return;
      // Destination: pickup si chauffeur en route, dropoff si course en cours
      const dest = ['accepted', 'driver_arriving'].includes(activeRide.status)
        ? activeRide.pickup
        : activeRide.dropoff;
      if (!dest?.lat) return;

      const ds = new window.google.maps.DirectionsService();
      ds.route({
        origin: driverLocation,
        destination: { lat: dest.lat, lng: dest.lng },
        travelMode: window.google.maps.TravelMode.DRIVING
      }, (result, status) => {
        if (status === 'OK') {
          setDriverRoute(result);
        }
      });
    }, [driverLocation, activeRide?.status]);

    // ——————————————————————————
    // GOOGLE MAPS — useJsApiLoader is at top-level component
    // Map is rendered inline in JSX using GoogleMap component
    // ——————————————————————————

    // Styles
    const cardStyle = { background: 'white', borderRadius: '16px', padding: '1rem', marginBottom: '0.75rem', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' };
    const inputStyle = { width: '100%', padding: '12px 16px', border: '2px solid #E9EBEF', borderRadius: '12px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' };

    const mapContainerStyle = { width: '100%', height: '100%' };
    const mapOptions = {
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    };

    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', background: '#f8f9fa' }}>

        {taxiError && (
          <div style={{ position: 'absolute', top: '1rem', left: '1rem', right: '1rem', zIndex: 1100, background: '#fef2f2', border: '1px solid #FEE2E2', borderRadius: '12px', padding: '12px 16px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}
            onClick={() => setTaxiError('')}
          >
            <AlertCircle size={18} /> {taxiError}
          </div>
        )}

        {/* ═══════════════════════════════════════════
            STEP: MAP — Sélection des adresses
        ═══════════════════════════════════════════ */}
        {taxiStep === 'map' && (
          <>
            {/* Carte plein écran — Google Maps */}
            <div style={{ flex: 1, position: 'relative' }}>
              {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={pickupCoords || BUKAVU_CENTER}
                zoom={pickupCoords ? 16 : 14}
                options={mapOptions}
                onLoad={onMapLoad}
                onClick={async (e) => {
                  const lat = e.latLng.lat();
                  const lng = e.latLng.lng();
                  const addr = await reverseGeocode(lat, lng);
                  if (activeInput === 'pickup') {
                    setPickupCoords({ lat, lng });
                    setPickupAddress(addr);
                    setPickupSearch(addr);
                    setPickupSuggestions([]);
                    setGpsAccuracy(null);
                    if (!dropoffCoords) setActiveInput('dropoff');
                  } else if (activeInput === 'dropoff') {
                    setDropoffCoords({ lat, lng });
                    setDropoffAddress(addr);
                    setDropoffSearch(addr);
                    setDropoffSuggestions([]);
                    setActiveInput(null);
                  }
                }}
              >
                {/* GPS accuracy circle */}
                {pickupCoords && gpsAccuracy && gpsAccuracy > 10 && (
                  <Circle
                    center={pickupCoords}
                    radius={gpsAccuracy}
                    options={{ strokeColor: '#4285F4', strokeWeight: 1.5, strokeOpacity: 0.4, fillColor: '#4285F4', fillOpacity: 0.12 }}
                  />
                )}

                {/* Pickup marker — draggable */}
                {pickupCoords && (
                  <Marker
                    position={pickupCoords}
                    draggable={true}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png', scaledSize: window.google ? new window.google.maps.Size(40, 40) : undefined }}
                    label={{ text: 'A', color: 'white', fontWeight: 'bold' }}
                    onDragEnd={async (e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();
                      const addr = await reverseGeocode(lat, lng);
                      setPickupCoords({ lat, lng });
                      setPickupAddress(addr);
                      setPickupSearch(addr);
                      setGpsAccuracy(null);
                    }}
                  />
                )}

                {/* Dropoff marker — draggable */}
                {dropoffCoords && (
                  <Marker
                    position={dropoffCoords}
                    draggable={true}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png', scaledSize: window.google ? new window.google.maps.Size(40, 40) : undefined }}
                    label={{ text: 'B', color: 'white', fontWeight: 'bold' }}
                    onDragEnd={async (e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();
                      const addr = await reverseGeocode(lat, lng);
                      setDropoffCoords({ lat, lng });
                      setDropoffAddress(addr);
                      setDropoffSearch(addr);
                    }}
                  />
                )}

                {/* Route drawn by DirectionsRenderer */}
                {directionsResult && (
                  <DirectionsRenderer
                    directions={directionsResult}
                    options={{ suppressMarkers: true, polylineOptions: { strokeColor: '#F54119', strokeWeight: 5, strokeOpacity: 0.8 } }}
                  />
                )}
              </GoogleMap>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F4F5F7' }}>
                  <div style={{ textAlign: 'center', color: '#5C636E' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🗺️</div>
                    <div>Chargement de la carte...</div>
                  </div>
                </div>
              )}

              {/* Bouton retour sur la carte */}
              <button
                onClick={() => setCurrentPage('home')}
                style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 1000, background: 'white', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer' }}
              >
                <ArrowLeft size={22} color="#1C1C1C" />
              </button>

              {/* Bouton historique */}
              <button
                onClick={() => { setTaxiStep('history'); loadHistory(); }}
                style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 1000, background: 'white', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer' }}
              >
                <History size={20} color="#F54119" />
              </button>

              {/* Indication du mode sélection */}
              {activeInput && (
                <div style={{ position: 'absolute', top: '1rem', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: activeInput === 'pickup' ? '#22c55e' : '#DC2626', color: 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  {activeInput === 'pickup' ? '📍 Touchez ou glissez le marqueur' : '🏁 Touchez ou glissez le marqueur'}
                </div>
              )}
            </div>

            {/* Panel d'adresses en bas (style Uber) */}
            <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: '1.25rem', boxShadow: '0 -4px 20px rgba(0,0,0,0.1)', position: 'relative', zIndex: 1000, maxHeight: '55vh', overflowY: 'auto' }}>
              {/* Barre de tirage */}
              <div style={{ width: '40px', height: '4px', background: '#DCE0E6', borderRadius: '4px', margin: '0 auto 1rem' }} />

              <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: '700', color: '#1C1C1C' }}>
                Planifiez votre course
              </h3>

              {/* Input départ */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '14px', gap: '4px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e', border: '3px solid #166534' }} />
                  <div style={{ width: '2px', height: '24px', background: '#DCE0E6' }} />
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#DC2626', border: '3px solid #991b1b' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ position: 'relative', marginBottom: '8px' }}>
                    <input
                      style={{ ...inputStyle, borderColor: activeInput === 'pickup' ? '#22c55e' : '#E9EBEF', paddingRight: '40px' }}
                      placeholder="Lieu de prise en charge"
                      value={pickupSearch}
                      onFocus={() => handleInputFocus('pickup')}
                      onChange={e => handleSearchInput(e.target.value, 'pickup')}
                    />
                    <button
                      onClick={useCurrentLocation}
                      disabled={locatingUser}
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                      title="Ma position"
                    >
                      {locatingUser ? <Loader size={18} className="spin" color="#F54119" /> : <Crosshair size={18} color="#F54119" />}
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      style={{ ...inputStyle, borderColor: activeInput === 'dropoff' ? '#DC2626' : '#E9EBEF' }}
                      placeholder="Où allez-vous ?"
                      value={dropoffSearch}
                      onFocus={() => handleInputFocus('dropoff')}
                      onChange={e => handleSearchInput(e.target.value, 'dropoff')}
                    />
                  </div>
                </div>
              </div>

              {/* Suggestions list — Google Maps style */}
              {((pickupSuggestions.length > 0 && activeInput === 'pickup') || (dropoffSuggestions.length > 0 && activeInput === 'dropoff')) && (
                <div style={{ marginTop: '6px' }}>
                  {/* Scrollable suggestions */}
                  <div style={{ maxHeight: '300px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    {(activeInput === 'pickup' ? pickupSuggestions : dropoffSuggestions).map((s, i) => (
                      <div
                        key={s.placeId || `pop-${i}`}
                        onClick={() => selectSuggestion(s, activeInput)}
                        style={{
                          padding: '13px 8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          borderBottom: '1px solid #F4F5F7',
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {/* Icon */}
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          background: '#F4F5F7',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <MapPin size={16} color="#5C636E" />
                        </div>

                        {/* Name + address on same line, Google Maps style */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '6px', overflow: 'hidden' }}>
                          <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1C1C1C', whiteSpace: 'nowrap', flexShrink: 0, maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.mainText}
                          </span>
                          {s.secondaryText && (
                            <span style={{ fontSize: '0.82rem', color: '#9AA0A8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {s.secondaryText}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bouton Ma position */}
              {!pickupCoords && activeInput === 'pickup' && pickupSuggestions.length === 0 && (
                <button
                  onClick={useCurrentLocation}
                  disabled={locatingUser}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '14px 16px', marginTop: '8px', background: '#f0f9ff', border: '2px solid #bae6fd', borderRadius: '12px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600', color: '#C9350F' }}
                >
                  {locatingUser ? <Loader size={18} className="spin" /> : <Navigation size={18} />}
                  {locatingUser ? 'Localisation GPS en cours...' : 'Utiliser ma position actuelle'}
                </button>
              )}

              {/* Prix et confirmation quand les 2 points sont sélectionnés */}
              {pickupCoords && dropoffCoords && (
                <div style={{ marginTop: '1rem' }}>

                  {/* ══════ SÉLECTION DU TYPE DE COURSE (style Uber) ══════ */}
                  {allPrices && !loadingEstimate && (
                    <div style={{ marginBottom: '1rem' }}>
                      <p style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1C1C1C', marginBottom: '10px', textAlign: 'center' }}>
                        Choisissez votre type de course
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        {[
                          { key: 'covoiturage', emoji: '🤝', name: 'Covoiturage', desc: 'Partagez le taxi', color: '#059669', borderColor: '#10b981' },
                          { key: 'mbiyo', emoji: '🚖', name: 'Mbiyo', desc: 'Taxi privé', color: '#1C1C1C', borderColor: '#1C1C1C' },
                          { key: 'confort', emoji: '💺', name: 'Confort', desc: 'Meilleures conditions', color: '#C9350F', borderColor: '#FF7800' },
                          { key: 'premium', emoji: '⭐', name: 'Premium', desc: 'Véhicule haut de gamme', color: '#C9350F', borderColor: '#FFB900' },
                          { key: 'moto', emoji: '🏍️', name: 'Moto', desc: 'Rapide et économique', color: '#c2410c', borderColor: '#f97316' }
                        ].map((type, idx, arr) => {
                          const isSelected = rideType === type.key;
                          const price = allPrices[type.key]?.estimated_price;
                          return (
                            <button
                              key={type.key}
                              onClick={() => setRideType(type.key)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '14px 16px', cursor: 'pointer',
                                background: isSelected ? '#FFFFFF' : 'white',
                                border: isSelected ? `2.5px solid ${type.borderColor}` : '1px solid #E9EBEF',
                                borderRadius: idx === 0 ? '14px 14px 0 0' : idx === arr.length - 1 ? '0 0 14px 14px' : '0',
                                marginTop: idx > 0 ? '-1px' : '0',
                                position: 'relative', zIndex: isSelected ? 2 : 1,
                                transition: 'all 0.15s ease'
                              }}
                            >
                              <span style={{ fontSize: '1.6rem', width: '40px', textAlign: 'center' }}>{type.emoji}</span>
                              <div style={{ flex: 1, textAlign: 'left' }}>
                                <div style={{ fontWeight: '700', fontSize: '0.95rem', color: type.color }}>{type.name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>{type.desc}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontWeight: '800', fontSize: '1.05rem', color: '#1C1C1C' }}>
                                  {price?.toLocaleString('fr-FR')} <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#5C636E' }}>CDF</span>
                                </div>
                                {distanceKm && <div style={{ fontSize: '0.7rem', color: '#9AA0A8' }}>{Math.ceil(distanceKm * 3)} min</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Loading */}
                  {loadingEstimate && (
                    <div style={{ textAlign: 'center', padding: '1rem', color: '#F54119' }}>
                      <Loader size={24} className="spin" style={{ display: 'inline-block' }} />
                      <p style={{ margin: '8px 0 0', fontSize: '0.9rem' }}>Calcul du trajet...</p>
                    </div>
                  )}

                  {/* Distance + Prix */}
                  {priceEstimate && !loadingEstimate && rideType && (
                    <>

                      {/* Info distance */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '1px solid #F4F5F7' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Car size={20} color="#5C636E" />
                          <span style={{ color: '#5C636E', fontSize: '0.9rem' }}>{distanceKm} km</span>
                        </div>
                        {priceEstimate.is_night && (
                          <span style={{ fontSize: '0.75rem', background: '#fef3c7', color: '#C9350F', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' }}>🌙 Tarif nuit</span>
                        )}
                        {priceEstimate.has_subscriber_discount && (
                          <span style={{ fontSize: '0.75rem', background: '#d1fae5', color: '#059669', padding: '4px 10px', borderRadius: '20px', fontWeight: '600' }}>🎫 -{Math.round((priceEstimate.subscriber_discount_rate || 0.10) * 100)}%</span>
                        )}
                      </div>

                      {/* Prix card (style Uber) */}
                      <div style={{ background: '#1C1C1C', borderRadius: '16px', padding: '1.25rem', color: 'white', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '48px', height: '48px', background: '#414852', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Car size={28} color="#F54119" />
                            </div>
                            <div>
                              <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>
                                {rideType === 'covoiturage' ? 'Covoiturage' : rideType === 'mbiyo' ? 'Mbiyo' : rideType === 'confort' ? 'Confort' : 'Premium'}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#9AA0A8' }}>{distanceKm} km • {Math.ceil(distanceKm * 3)} min</div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>
                              {priceEstimate.estimated_price?.toLocaleString('fr-FR')}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>CDF</div>
                          </div>
                        </div>
                        {/* Breakdown — réductions et suppléments uniquement */}
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #414852', fontSize: '0.8rem', color: '#9AA0A8' }}>
                          {priceEstimate.breakdown?.night_surcharge > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#FFB900' }}>
                              <span>🌙 Supplément nuit</span>
                              <span>+{priceEstimate.breakdown.night_surcharge.toLocaleString('fr-FR')} CDF</span>
                            </div>
                          )}
                          {priceEstimate.breakdown?.subscriber_discount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#34d399' }}>
                              <span>🎫 Réduction abonné</span>
                              <span>-{priceEstimate.breakdown.subscriber_discount.toLocaleString('fr-FR')} CDF</span>
                            </div>
                          )}
                          {priceEstimate.breakdown?.ride_type_adjustment !== 0 && priceEstimate.breakdown?.ride_type_adjustment != null && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: priceEstimate.breakdown.ride_type_adjustment < 0 ? '#34d399' : '#f97316' }}>
                              <span>{rideType === 'covoiturage' ? '🤝 Réduction covoiturage' : rideType === 'confort' ? '💺 Supplément confort' : rideType === 'premium' ? '⭐ Supplément premium' : rideType === 'moto' ? '🏍️ Tarif moto' : ''}</span>
                              <span>{priceEstimate.breakdown.ride_type_adjustment > 0 ? '+' : ''}{priceEstimate.breakdown.ride_type_adjustment.toLocaleString('fr-FR')} CDF</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Mode de paiement */}
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '0.75rem' }}>
                        <button
                          onClick={() => setPaymentMethod('cash')}
                          style={{ flex: 1, padding: '14px', borderRadius: '12px', border: paymentMethod === 'cash' ? '2px solid #1C1C1C' : '2px solid #E9EBEF', background: paymentMethod === 'cash' ? '#FFFFFF' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '0.9rem', color: '#1C1C1C' }}
                        >
                          💵 Cash
                        </button>
                        <button
                          onClick={() => setPaymentMethod('mobile_money')}
                          style={{ flex: 1, padding: '14px', borderRadius: '12px', border: paymentMethod === 'mobile_money' ? '2px solid #1C1C1C' : '2px solid #E9EBEF', background: paymentMethod === 'mobile_money' ? '#FFFFFF' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontWeight: '600', fontSize: '0.9rem', color: '#1C1C1C' }}
                        >
                          📱 Mobile Money
                        </button>
                      </div>

                      {/* Bouton commander */}
                      <button
                        onClick={requestRide}
                        disabled={loadingRequest}
                        style={{ width: '100%', padding: '16px', background: '#1C1C1C', color: 'white', border: 'none', borderRadius: '14px', fontSize: '1.05rem', fontWeight: '700', cursor: loadingRequest ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', opacity: loadingRequest ? 0.7 : 1 }}
                      >
                        {loadingRequest ? <><Loader size={20} className="spin" /> Recherche...</> : <><Car size={22} /> Commander {rideType === 'covoiturage' ? 'un Covoiturage' : rideType === 'confort' ? 'un Confort' : rideType === 'premium' ? 'un Premium' : rideType === 'moto' ? 'une Moto' : 'un Mbiyo'}</>}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════
            STEP: SEARCHING — Recherche de chauffeur
        ═══════════════════════════════════════════ */}
        {taxiStep === 'searching' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '2rem', background: 'linear-gradient(180deg, #F54119 0%, #FF5500 100%)', color: 'white' }}>
            {/* Animated radar */}
            <div style={{ position: 'relative', width: '160px', height: '160px', marginBottom: '2rem' }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.2)', animation: 'taxiRadar 2s ease-out infinite' }} />
              <div style={{ position: 'absolute', inset: '20px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.3)', animation: 'taxiRadar 2s ease-out infinite 0.5s' }} />
              <div style={{ position: 'absolute', inset: '40px', borderRadius: '50%', border: '3px solid rgba(255,255,255,0.4)', animation: 'taxiRadar 2s ease-out infinite 1s' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                🚖
              </div>
            </div>

            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.3rem', fontWeight: '700' }}>Recherche d'un chauffeur</h3>
            <p style={{ opacity: 0.8, marginBottom: '0.5rem', textAlign: 'center', fontSize: '0.9rem' }}>
              {activeRide?.pickup?.address} → {activeRide?.dropoff?.address}
            </p>

            {/* Timer */}
            <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem', fontFamily: 'monospace' }}>
              {Math.floor(searchTimer / 60)}:{String(searchTimer % 60).padStart(2, '0')}
            </div>

            {/* Progress bar */}
            <div style={{ width: '100%', maxWidth: '280px', background: 'rgba(255,255,255,0.2)', borderRadius: '999px', height: '6px', marginBottom: '1.5rem' }}>
              <div style={{ width: `${(searchTimer / 300) * 100}%`, height: '100%', background: 'white', borderRadius: '999px', transition: 'width 5s linear' }} />
            </div>

            <p style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              Prix: <strong>{activeRide?.estimated_price?.toLocaleString('fr-FR')} CDF</strong>
            </p>

            <button
              onClick={requestCancelWithEstimate}
              disabled={loadingCancelEstimate}
              style={{ marginTop: '2rem', padding: '14px 40px', border: '2px solid rgba(255,255,255,0.5)', background: 'transparent', color: 'white', borderRadius: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', opacity: loadingCancelEstimate ? 0.6 : 1 }}
            >
              {loadingCancelEstimate ? 'Calcul...' : 'Annuler'}
            </button>

            <style>{`
              @keyframes taxiRadar {
                0% { transform: scale(1); opacity: 1; }
                100% { transform: scale(2.5); opacity: 0; }
              }
            `}</style>
          </div>
        )}

        {/* ═══════════════════════════════════════════
            STEP: ACTIVE — Course en cours
        ═══════════════════════════════════════════ */}
        {taxiStep === 'active' && activeRide && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {/* Map temps réel avec position chauffeur */}
            {activeRide.pickup?.lat && activeRide.dropoff?.lat && isLoaded && (
              <div style={{ height: '280px', flexShrink: 0, position: 'relative' }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={driverLocation || {
                    lat: (activeRide.pickup.lat + activeRide.dropoff.lat) / 2,
                    lng: (activeRide.pickup.lng + activeRide.dropoff.lng) / 2
                  }}
                  zoom={14}
                  options={{ ...mapOptions, gestureHandling: 'cooperative' }}
                  onLoad={(map) => {
                    clientMapRef.current = map;
                    // Fit bounds
                    const bounds = new window.google.maps.LatLngBounds();
                    bounds.extend({ lat: activeRide.pickup.lat, lng: activeRide.pickup.lng });
                    bounds.extend({ lat: activeRide.dropoff.lat, lng: activeRide.dropoff.lng });
                    if (driverLocation) bounds.extend(driverLocation);
                    map.fitBounds(bounds, 40);
                  }}
                >
                  {/* Point de départ A */}
                  <Marker
                    position={{ lat: activeRide.pickup.lat, lng: activeRide.pickup.lng }}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png', scaledSize: window.google ? new window.google.maps.Size(32, 32) : undefined }}
                    label={{ text: 'A', color: 'white', fontWeight: 'bold', fontSize: '11px' }}
                  />
                  {/* Destination B */}
                  <Marker
                    position={{ lat: activeRide.dropoff.lat, lng: activeRide.dropoff.lng }}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png', scaledSize: window.google ? new window.google.maps.Size(32, 32) : undefined }}
                    label={{ text: 'B', color: 'white', fontWeight: 'bold', fontSize: '11px' }}
                  />
                  {/* Position chauffeur en temps réel */}
                  {driverLocation && (
                    <Marker
                      position={driverLocation}
                      icon={{
                        path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 7,
                        fillColor: '#FF7800',
                        fillOpacity: 1,
                        strokeColor: '#C9350F',
                        strokeWeight: 2,
                        rotation: 0
                      }}
                      title="Votre chauffeur"
                    />
                  )}
                  {/* Route chauffeur → destination */}
                  {driverRoute && (
                    <DirectionsRenderer
                      directions={driverRoute}
                      options={{
                        suppressMarkers: true,
                        polylineOptions: { strokeColor: '#FF7800', strokeWeight: 4, strokeOpacity: 0.8 }
                      }}
                    />
                  )}
                </GoogleMap>
                {/* Badge statut sur la carte */}
                <div style={{
                  position: 'absolute', bottom: '10px', left: '10px', right: '10px',
                  background: 'rgba(0,0,0,0.7)', color: 'white', padding: '8px 12px',
                  borderRadius: '10px', fontSize: '0.8rem', fontWeight: '600',
                  textAlign: 'center', backdropFilter: 'blur(4px)'
                }}>
                  {statusLabels[activeRide.status] || activeRide.status}
                </div>
              </div>
            )}

            <div style={{ flex: 1, padding: '1rem', overflow: 'auto' }}>
              {/* Alerte chauffeur arrivé */}
              {driverArrivedAlert && (
                <div style={{
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  color: 'white',
                  padding: '16px',
                  borderRadius: '16px',
                  marginBottom: '12px',
                  textAlign: 'center',
                  animation: 'pulseGlow 1.5s ease-in-out infinite',
                  boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '4px' }}>🚖</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: '800' }}>Votre chauffeur est arrivé !</div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.95, marginTop: '4px' }}>
                    Rejoignez-le au point de prise en charge
                  </div>
                  <button onClick={async () => {
                    setDriverArrivedAlert(false);
                    // Auto-send chat message
                    if (activeRide) {
                      try {
                        await apiService.post(`/taxi/${activeRide.ride_id}/messages`, { text: "👋 OK, j'arrive !" });
                        loadMessages();
                      } catch (e) { /* ignore */ }
                    }
                  }} style={{
                    marginTop: '10px',
                    background: 'rgba(255,255,255,0.25)',
                    border: 'none',
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}>OK, j'arrive</button>
                </div>
              )}

              {/* Status banner */}
              <div style={{ ...cardStyle, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#166534', marginBottom: '6px' }}>
                  {statusLabels[activeRide.status] || activeRide.status}
                </div>
                <div style={{ color: '#414852', fontSize: '0.85rem' }}>
                  <strong>De:</strong> {activeRide.pickup?.address}
                </div>
                <div style={{ color: '#414852', fontSize: '0.85rem' }}>
                  <strong>À:</strong> {activeRide.dropoff?.address}
                </div>
                <div style={{ marginTop: '6px', fontWeight: '700', color: '#C9350F', fontSize: '1.1rem' }}>
                  {activeRide.estimated_price?.toLocaleString('fr-FR')} CDF
                </div>
              </div>

              {/* Driver info */}
              {activeRide.driver && (
                <div style={cardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#F54119', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.2rem', fontWeight: '700', overflow: 'hidden', flexShrink: 0 }}>
                      {activeRide.driver.photo ? (
                        <img src={activeRide.driver.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        activeRide.driver.name?.charAt(0) || '?'
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '1.05rem' }}>{activeRide.driver.name}</div>
                      {activeRide.driver.average_rating != null && (
                        <div style={{ color: '#FFB900', fontSize: '0.8rem' }}>⭐ {ratingText(activeRide.driver.average_rating, activeRide.driver.rating_count)}{activeRide.driver.rating_count >= 5 ? '/5' : ''}</div>
                      )}
                    </div>
                  </div>

                  {/* Infos véhicule - bien visibles */}
                  <div style={{ marginTop: '10px', background: '#f0f4ff', borderRadius: '12px', padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <Car size={16} style={{ color: '#C9350F' }} />
                      <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1E2229' }}>
                        {activeRide.driver.vehicle_brand} {activeRide.driver.vehicle_model}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#414852' }}>
                      <span>🎨 {activeRide.driver.vehicle_color || 'N/A'}</span>
                      <span style={{ fontWeight: '700', color: '#12151A', letterSpacing: '1px', background: '#E9EBEF', padding: '2px 8px', borderRadius: '6px' }}>
                        {activeRide.driver.license_plate || 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    {activeRide.driver.phone && (
                      <a href={`tel:${activeRide.driver.phone}`} style={{ flex: 1, padding: '10px', background: '#ecfdf5', color: '#059669', borderRadius: '12px', textAlign: 'center', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.9rem' }}>
                        <Phone size={18} /> Appeler
                      </a>
                    )}
                    <button
                      onClick={() => setShowChat(!showChat)}
                      style={{ flex: 1, padding: '10px', background: showChat ? '#F54119' : '#f0f3ff', color: showChat ? 'white' : '#F54119', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.9rem', position: 'relative' }}
                    >
                      <MessageCircle size={18} /> Chat
                      {chatNotifBadge && !showChat && (
                        <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: '14px', height: '14px', background: '#DC2626', borderRadius: '50%', border: '2px solid white', animation: 'pulseGlow 1.5s ease-in-out infinite' }} />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Chat */}
              {showChat && (
                <div style={cardStyle}>
                  <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>💬 Chat</h4>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '10px', border: '1px solid #E9EBEF', borderRadius: '12px', padding: '10px' }}>
                    {chatMessages.length === 0 && (
                      <p style={{ color: '#9AA0A8', textAlign: 'center', fontSize: '0.85rem' }}>Aucun message</p>
                    )}
                    {chatMessages.map((msg, i) => (
                      <div key={i} style={{ marginBottom: '8px', textAlign: msg.sender_type === 'client' ? 'right' : 'left' }}>
                        <div style={{
                          display: 'inline-block', padding: '8px 12px', borderRadius: '12px', maxWidth: '80%', fontSize: '0.85rem',
                          background: msg.sender_type === 'client' ? '#F54119' : '#F4F5F7',
                          color: msg.sender_type === 'client' ? 'white' : '#414852'
                        }}>
                          {msg.text}
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#9AA0A8', marginTop: '2px' }}>
                          {new Date(msg.sent_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder="Votre message..."
                      value={chatText}
                      onChange={e => setChatText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    />
                    <button
                      onClick={sendMessage}
                      style={{ padding: '12px', background: '#F54119', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer' }}
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* Cancel button */}
              {['searching', 'accepted', 'driver_arriving'].includes(activeRide.status) && (
                <button
                  onClick={requestCancelWithEstimate}
                  disabled={loadingCancelEstimate}
                  style={{ width: '100%', padding: '14px', border: '2px solid #DC2626', background: 'transparent', color: '#DC2626', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', marginTop: '0.5rem', opacity: loadingCancelEstimate ? 0.6 : 1 }}
                >
                  {loadingCancelEstimate ? 'Calcul des frais...' : 'Annuler la course'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════
            STEP: COMPLETED — Notation obligatoire
        ═══════════════════════════════════════════ */}
        {taxiStep === 'completed' && activeRide && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflow: 'auto', padding: '2rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>🎉</div>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.3rem' }}>Course terminée !</h3>
                <p style={{ color: '#5C636E', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  {activeRide.pickup?.address} → {activeRide.dropoff?.address}
                </p>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#1C1C1C', marginBottom: '1.5rem' }}>
                  {(activeRide.final_price || activeRide.estimated_price)?.toLocaleString('fr-FR')} CDF
                </div>

                {/* Mobile Money payment */}
                {activeRide.payment_method === 'mobile_money' && activeRide.payment_status !== 'paid' && !taxiPaymentCompleted && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fef3c7', borderRadius: '12px', border: '1px solid #FFB900' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                      📱 Paiement Mobile Money requis
                    </div>
                    <p style={{ color: '#78350f', fontSize: '0.85rem', marginBottom: '12px' }}>
                      Veuillez effectuer le paiement de {(activeRide.final_price || activeRide.estimated_price)?.toLocaleString('fr-FR')} CDF
                    </p>
                    <button
                      onClick={() => setShowTaxiPaymentModal(true)}
                      style={{ padding: '12px 28px', background: '#FFB900', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      💳 Payer maintenant
                    </button>
                  </div>
                )}
                {taxiPaymentCompleted && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ecfdf5', borderRadius: '12px', border: '1px solid #6ee7b7' }}>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#065f46' }}>✅ Paiement effectué !</div>
                  </div>
                )}

                <PaymentModal
                  isOpen={showTaxiPaymentModal}
                  planName="Course taxi"
                  amount={activeRide.final_price || activeRide.estimated_price}
                  duration={0}
                  onSuccess={(paymentData) => {
                    setShowTaxiPaymentModal(false);
                    setTaxiPaymentCompleted(true);
                  }}
                  onCancel={() => setShowTaxiPaymentModal(false)}
                  rideId={activeRide.ride_id}
                />

                <h4 style={{ marginBottom: '0.75rem', color: '#414852' }}>Évaluez votre chauffeur</h4>
                <p style={{ color: '#9AA0A8', fontSize: '0.8rem', marginBottom: '0.75rem' }}>Facultatif — si vous ne notez pas maintenant, nous vous le rappellerons une fois demain.</p>

                {/* Étoiles */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '1rem' }}>
                  {[1, 2, 3, 4, 5].map(v => (
                    <button
                      key={v}
                      onClick={() => setRatingValue(v)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '2.2rem', opacity: v <= ratingValue ? 1 : 0.25, transition: 'all 0.2s', transform: v <= ratingValue ? 'scale(1.1)' : 'scale(1)' }}
                    >
                      ⭐
                    </button>
                  ))}
                </div>

                {/* Tags — affichés après sélection des étoiles */}
                {ratingValue > 0 && ratingCriteria && (
                  <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                    <p style={{ fontWeight: '600', fontSize: '0.85rem', color: '#414852', marginBottom: '8px' }}>
                      {ratingValue >= 4 ? '👍 Qu\'avez-vous apprécié ?' : '👎 Qu\'est-ce qui n\'allait pas ?'}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(ratingValue >= 4 ? ratingCriteria.positive : ratingCriteria.negative).map(tag => {
                        const selected = ratingTags.includes(tag.key);
                        return (
                          <button
                            key={tag.key}
                            onClick={() => {
                              setRatingTags(prev =>
                                selected ? prev.filter(t => t !== tag.key) : [...prev, tag.key]
                              );
                            }}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '20px',
                              border: selected ? '2px solid #1C1C1C' : '2px solid #E9EBEF',
                              background: selected ? '#1C1C1C' : 'white',
                              color: selected ? 'white' : '#414852',
                              fontSize: '0.82rem',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {tag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Commentaire */}
                <textarea
                  placeholder="Commentaire (optionnel)"
                  value={ratingComment}
                  onChange={e => setRatingComment(e.target.value)}
                  style={{ ...inputStyle, minHeight: '70px', resize: 'vertical', marginBottom: '1rem', width: '100%' }}
                />

                {taxiError && (
                  <p style={{ color: '#DC2626', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{taxiError}</p>
                )}

                <button
                  onClick={submitRating}
                  disabled={!ratingValue}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: ratingValue ? '#1C1C1C' : '#DCE0E6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '14px',
                    fontWeight: '600',
                    cursor: ratingValue ? 'pointer' : 'not-allowed',
                    fontSize: '0.95rem'
                  }}
                >
                  Envoyer l'évaluation
                </button>
                <button onClick={resetAfterRide} style={{ width: '100%', padding: '10px', marginTop: '8px', background: 'none', border: 'none', color: '#9AA0A8', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════
            STEP: HISTORY — Historique
        ═══════════════════════════════════════════ */}
        {taxiStep === 'history' && (
          <div style={{ flex: 1, padding: '1rem', overflow: 'auto', background: 'white' }}>
            <button
              onClick={resetToNewRide}
              style={{ background: 'none', border: 'none', color: '#F54119', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '1rem', fontSize: '0.95rem' }}
            >
              <ArrowLeft size={18} /> Retour
            </button>

            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>📋 Historique des courses</h3>

            {loadingHistory && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#F54119' }}>
                <Loader size={24} className="spin" />
              </div>
            )}

            {!loadingHistory && rideHistory.length === 0 && (
              <div style={{ ...cardStyle, textAlign: 'center', color: '#9AA0A8', padding: '2rem' }}>
                <Car size={40} style={{ marginBottom: '0.5rem', opacity: 0.3 }} />
                <p>Aucune course pour le moment</p>
              </div>
            )}

            {rideHistory.map(ride => (
              <div key={ride.ride_id} style={{ ...cardStyle, borderLeft: ride.status === 'completed' ? '4px solid #22c55e' : '4px solid #DC2626' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{statusLabels[ride.status] || ride.status}</span>
                  <span style={{ color: '#5C636E', fontSize: '0.75rem' }}>
                    {new Date(ride.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#414852' }}>
                  {ride.pickup?.address} → {ride.dropoff?.address}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                  <span style={{ fontWeight: '700', color: '#C9350F' }}>
                    {(ride.final_price || ride.estimated_price)?.toLocaleString('fr-FR')} CDF
                  </span>
                  {ride.distance_km && <span style={{ color: '#9AA0A8', fontSize: '0.75rem' }}>{ride.distance_km} km</span>}
                </div>
                {ride.driver_name && (
                  <div style={{ fontSize: '0.75rem', color: '#5C636E', marginTop: '4px' }}>Chauffeur: {ride.driver_name}</div>
                )}
                {ride.rating_by_client && (
                  <div style={{ color: '#FFB900', fontSize: '0.8rem', marginTop: '2px' }}>
                    {'⭐'.repeat(ride.rating_by_client)} ({ride.rating_by_client}/5)
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ═══════════════════════════════════════════
            MODAL: Confirmation annulation avec frais
        ═══════════════════════════════════════════ */}
        {showCancelConfirm && cancelEstimate && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem' }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '2rem', maxWidth: '380px', width: '100%', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
              {cancelEstimate.fee_total === 0 ? (
                <>
                  <h3 style={{ marginBottom: '0.5rem', color: '#1C1C1C' }}>Annuler cette course ?</h3>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Aucun frais d'annulation ne sera appliqué.</p>
                </>
              ) : (
                <>
                  <h3 style={{ marginBottom: '0.5rem', color: '#1C1C1C' }}>Frais d'annulation</h3>
                  <p style={{ color: '#5C636E', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    Des frais d'annulation s'appliqueront :
                  </p>
                  <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#dc2626', marginBottom: '0.5rem' }}>
                    {cancelEstimate.fee_total?.toLocaleString('fr-FR')} CDF
                  </div>
                  {cancelEstimate.breakdown?.night_surcharge_applied && (
                    <div style={{ fontSize: '0.8rem', color: '#FF9B00', marginBottom: '0.25rem' }}>🌙 Majoration nuit appliquée</div>
                  )}
                  <p style={{ color: '#9AA0A8', fontSize: '0.8rem', marginBottom: '1.5rem' }}>
                    Ce montant sera ajouté à votre prochaine course.
                  </p>
                </>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => { setShowCancelConfirm(false); setCancelEstimate(null); }}
                  style={{ flex: 1, padding: '14px', background: '#F4F5F7', color: '#414852', border: 'none', borderRadius: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem' }}
                >
                  Garder la course
                </button>
                <button
                  onClick={cancelRide}
                  style={{ flex: 1, padding: '14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem' }}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="taxi-app">
      <IOSInstallBanner />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .taxi-app {
          font-family: 'Poppins', sans-serif;
          min-height: 100vh;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          position: relative;
          overflow-x: hidden;
        }

        .taxi-app::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
          pointer-events: none;
        }

        .mobile-header {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: sticky;
          top: 0;
          z-index: 100;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: white;
          font-weight: 700;
          font-size: 1.3rem;
          font-family: 'Space Mono', monospace;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .lang-selector {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          backdrop-filter: blur(10px);
        }

        .menu-btn {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.5rem;
        }

        .mobile-menu {
          position: fixed;
          top: 0;
          right: ${menuOpen ? '0' : '-100%'};
          width: 80%;
          max-width: 300px;
          height: 100dvh; /* vh mobile ment (barre d'URL) */
          background: rgba(20, 20, 40, 0.98);
          backdrop-filter: blur(20px);
          padding: 2rem 1.5rem calc(2rem + env(safe-area-inset-bottom, 0px));
          transition: right 260ms var(--mb-ease-out, ease);
          z-index: 200;
          border-left: 1px solid rgba(255, 255, 255, 0.1);
          /* Le tiroir défile LUI-MÊME ; le geste ne chaîne jamais vers la page */
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }

        .menu-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
        }

        .menu-nav {
          margin-top: 3rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .menu-item {
          padding: 1rem;
          color: white;
          text-decoration: none;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s;
          cursor: pointer;
        }

        .menu-item:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateX(5px);
        }

        .menu-item.active {
          background: rgba(102, 126, 234, 0.3);
        }

        .app-container {
          padding: 2rem 1.5rem;
          max-width: 680px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        /* PLEINE PAGE — la colonne s'élargit sur grand écran (charte : aéré, fluide) */
        @media (min-width: 900px) {
          .app-container { max-width: 1240px; padding: 2.2rem 2rem; }
          .plans-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (min-width: 1280px) {
          .plans-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
        /* Le module Market gère sa propre largeur : neutraliser le conteneur autour */
        .app-container:has(.mk-root) { max-width: none; padding: 0; }

        .hero-section {
          text-align: center;
          margin-bottom: 2rem;
          animation: fadeInUp 0.6s ease;
        }

        .hero-section h1 {
          color: white;
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
        }

        .quick-qr {
          background: white;
          border-radius: 24px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 1.5rem;
          align-items: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          animation: fadeInUp 0.6s ease 0.1s both;
        }

        .mini-qr {
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          padding: 1rem;
          border-radius: 16px;
          color: white;
        }

        .quick-qr-info h3 {
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
          color: #1C1C1C;
        }

        .plan-name {
          background: linear-gradient(135deg, #FFB900 0%, #FF9B00 100%);
          color: white;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          display: inline-block;
          font-size: 0.85rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .btn-primary {
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          color: white;
          border: none;
          padding: 0.8rem 1.5rem;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          transition: all 0.2s;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.95);
          padding: 1.5rem;
          border-radius: 20px;
          display: flex;
          gap: 1rem;
          align-items: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          animation: fadeInUp 0.6s ease 0.2s both;
        }

        .stat-card svg {
          color: #F54119;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #1C1C1C;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #5C636E;
        }

        .qr-page {
          animation: fadeIn 0.4s ease;
        }

        .qr-header {
          margin-bottom: 2rem;
        }

        .qr-header h2 {
          color: white;
          font-size: 1.8rem;
          margin-bottom: 1rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .qr-container {
          display: flex;
          justify-content: center;
        }

        .qr-card {
          background: white;
          border-radius: 32px;
          padding: 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          width: 100%;
          max-width: 400px;
        }

        .qr-user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 2px solid #F4F5F7;
        }

        .user-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.8rem;
          font-weight: 700;
        }

        .qr-user-info h3 {
          font-size: 1.2rem;
          color: #1C1C1C;
          margin-bottom: 0.3rem;
        }

        .plan-badge {
          background: #FFB900;
          color: white;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
        }

        .qr-code-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 2rem;
          background: linear-gradient(135deg, #F4F5F7 0%, #E9EBEF 100%);
          border-radius: 20px;
          margin-bottom: 1.5rem;
        }

        .qr-code-text {
          font-family: 'Space Mono', monospace;
          font-size: 1.1rem;
          font-weight: 700;
          color: #1C1C1C;
          letter-spacing: 2px;
        }

        .qr-instructions {
          text-align: center;
          padding: 1rem;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 12px;
          margin-bottom: 1rem;
        }

        .qr-instructions p {
          color: #F54119;
          font-weight: 600;
        }

        .qr-validity {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: #5C636E;
          font-weight: 600;
        }

        .subscription-page h2 {
          color: white;
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .billing-toggle {
          display: flex;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.5rem;
          border-radius: 16px;
          margin-bottom: 2rem;
          backdrop-filter: blur(10px);
        }

        .billing-toggle button {
          flex: 1;
          padding: 0.8rem;
          border: none;
          background: transparent;
          color: white;
          font-weight: 600;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
        }

        .billing-toggle button.active {
          background: white;
          color: #F54119;
        }

        .save-badge {
          background: #10b981;
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 8px;
          font-size: 0.7rem;
          margin-left: 0.3rem;
        }

        .plans-grid {
          display: grid;
          gap: 1.5rem;
        }

        .plan-card {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          border-top: 4px solid;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s;
        }

        .plan-card:hover {
          transform: translateY(-5px);
        }

        .plan-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .plan-card h3 {
          font-size: 1.3rem;
          color: #1C1C1C;
          margin-bottom: 0.5rem;
        }

        .plan-description {
          color: #5C636E;
          margin-bottom: 1.5rem;
        }

        .plan-price {
          display: flex;
          align-items: baseline;
          gap: 0.3rem;
          margin-bottom: 1.5rem;
        }

        .price {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1C1C1C;
        }

        .currency {
          font-size: 1.2rem;
          font-weight: 600;
          color: #5C636E;
        }

        .period {
          font-size: 0.9rem;
          color: #9AA0A8;
        }

        .plan-features {
          list-style: none;
          margin-bottom: 1.5rem;
        }

        .plan-features li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.8rem;
          color: #414852;
        }

        .subscribe-btn {
          width: 100%;
          padding: 1rem;
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Poppins', sans-serif;
          font-size: 1rem;
          transition: all 0.2s;
        }

        .subscribe-btn:hover {
          transform: scale(1.02);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }

        .routes-page h2 {
          color: white;
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
        }

        .routes-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .route-card {
          background: white;
          border-radius: 16px;
          padding: 1.5rem;
          display: flex;
          gap: 1rem;
          align-items: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.2s;
        }

        .route-card:hover {
          transform: translateX(5px);
        }

        .route-card.inactive {
          opacity: 0.6;
        }

        .route-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .route-info {
          flex: 1;
        }

        .route-info h3 {
          color: #1C1C1C;
          font-size: 1.05rem;
          margin-bottom: 0.3rem;
        }

        .route-meta {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          color: #5C636E;
          font-size: 0.85rem;
        }

        /* Styles pour les pages d'authentification */
        .auth-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .auth-container {
          width: 100%;
          max-width: 450px;
        }

        .auth-card {
          background: white;
          border-radius: 24px;
          padding: 3rem 2.5rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: fadeInUp 0.5s ease;
        }

        .auth-card h1 {
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 0.5rem;
          color: #F54119;
        }

        .auth-card h2 {
          font-size: 1.8rem;
          text-align: center;
          margin-bottom: 0.5rem;
          color: #1C1C1C;
        }

        .auth-subtitle {
          text-align: center;
          color: #5C636E;
          margin-bottom: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          color: #414852;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.9rem 1rem;
          border: 2px solid #E9EBEF;
          border-radius: 12px;
          font-size: 1rem;
          font-family: 'Poppins', sans-serif;
          transition: all 0.2s;
        }

        .phone-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .phone-input-wrapper input {
          padding-left: 3rem;
        }

        .phone-icon {
          position: absolute;
          left: 1rem;
          color: #5C636E;
        }

        .input-hint {
          display: block;
          margin-top: 0.4rem;
          color: #5C636E;
          font-size: 0.8rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #F54119;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .btn-auth {
          width: 100%;
          padding: 1rem;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
          margin-top: 1rem;
        }

        .btn-auth:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
        }

        .auth-switch {
          text-align: center;
          margin-top: 1.5rem;
          color: #5C636E;
        }

        .auth-switch span {
          color: #F54119;
          font-weight: 600;
          cursor: pointer;
          text-decoration: underline;
        }

        .error-message {
          background: #fee2e2;
          color: #dc2626;
          padding: 0.8rem 1rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4); transform: scale(1); }
          50% { box-shadow: 0 4px 30px rgba(16, 185, 129, 0.7); transform: scale(1.02); }
        }

        @media (min-width: 768px) {
          .plans-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        /* Styles additionnels pour la capture photo */

.required {
  color: #dc2626;
  font-weight: bold;
}

.optional {
  color: #9AA0A8;
  font-weight: normal;
  font-size: 0.85rem;
}

.photo-capture-area {
  margin-top: 0.5rem;
}

.photo-placeholder {
  border: 3px dashed #DCE0E6;
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #FFFFFF;
}

.photo-placeholder:hover {
  border-color: #F54119;
  background: #F4F5F7;
}

.photo-placeholder svg {
  color: #9AA0A8;
  margin-bottom: 1rem;
}

.photo-placeholder p {
  color: #414852;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.photo-placeholder small {
  color: #dc2626;
  font-size: 0.85rem;
}

.photo-preview {
  text-align: center;
}

.photo-preview img {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #F54119;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.change-photo-btn {
  background: #F4F5F7;
  border: 2px solid #E9EBEF;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  color: #414852;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Poppins', sans-serif;
}

.change-photo-btn:hover {
  background: #E9EBEF;
  border-color: #F54119;
  color: #F54119;
}
      `}</style>

      {isAuthenticated && (
        <div className="mobile-header">
          <div className="logo">
            <img src="/logo192.png" alt="" style={{ width: 28, height: 28, borderRadius: 7 }} />
            Mbiyo
          </div>
          <div className="header-actions">
            <button className="menu-btn" style={{ position: 'relative' }} onClick={() => { setShowClientNotifs(!showClientNotifs); if (!showClientNotifs) loadClientNotifications(); }}>
              <Bell size={22} />
              {clientUnreadCount > 0 && (
                <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#DC2626', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>{clientUnreadCount > 9 ? '9+' : clientUnreadCount}</span>
              )}
            </button>
            <select 
              className="lang-selector" 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="sw">SW</option>
              <option value="ln">LN</option>
            </select>
            <button className="menu-btn" onClick={() => setMenuOpen(true)}>
              <Menu size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Notifications dropdown */}
      {isAuthenticated && showClientNotifs && (
        <div style={{ position: 'fixed', top: '60px', right: '10px', left: '10px', maxWidth: '400px', marginLeft: 'auto', background: 'white', borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', zIndex: 1100, maxHeight: '70vh', overflow: 'auto', border: '1px solid #E9EBEF' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #F4F5F7' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Notifications</h3>
            {clientUnreadCount > 0 && (
              <span style={{ background: '#DC2626', color: 'white', borderRadius: '20px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: '700' }}>{clientUnreadCount}</span>
            )}
          </div>
          {clientNotifications.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucune notification</div>
          ) : (
            clientNotifications.map(notif => (
              <div key={notif.notification_id || notif._id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 20px', borderBottom: '1px solid #FFFFFF', background: notif.read ? 'white' : '#f0f7ff' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FEE9E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.1rem' }}>
                  {notif.title?.includes('terminée') ? '🎉' : notif.title?.includes('annulée') ? '❌' : notif.title?.includes('commencé') ? '🟢' : notif.title?.includes('arrivé') ? '📍' : notif.title?.includes('accepté') || notif.title?.includes('trouvé') ? '🚗' : '🔔'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: notif.read ? '400' : '600', color: '#1C1C1C' }}>{notif.message || notif.title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9AA0A8', marginTop: '4px' }}>{(() => { const d = new Date(notif.created_at); const now = new Date(); const diff = Math.floor((now - d) / 60000); if (diff < 1) return "À l'instant"; if (diff < 60) return `Il y a ${diff} min`; if (diff < 1440) return `Il y a ${Math.floor(diff/60)}h`; return `Il y a ${Math.floor(diff/1440)}j`; })()}</div>
                </div>
                {!notif.read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FF7800', marginTop: '6px', flexShrink: 0 }} />}
              </div>
            ))
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #F4F5F7' }}>
            <button style={{ background: 'none', border: 'none', color: '#FF7800', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => { markAllClientNotifsRead(); }}>Tout marquer comme lu</button>
            <button style={{ background: 'none', border: 'none', color: '#FF7800', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }} onClick={() => setShowClientNotifs(false)}>Fermer</button>
          </div>
        </div>
      )}

      {isAuthenticated && (
        <div className="mobile-menu">
          <button className="menu-close" onClick={() => setMenuOpen(false)}>
            <X size={24} />
          </button>
          <nav className="menu-nav">
            <div
              className={`menu-item ${currentPage === 'landing' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('landing'); setMenuOpen(false); }}
            >
              ✨ Découvrir Mbiyo
            </div>
            <div 
              className={`menu-item ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('home'); setMenuOpen(false); }}
            >
              <MapPin size={20} />
              {t.home}
            </div>
            <div 
              className={`menu-item ${currentPage === 'qr' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('qr'); setMenuOpen(false); }}
            >
              <QrCode size={20} />
              {t.myQR}
            </div>
            <div 
              className={`menu-item ${currentPage === 'subscription' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('subscription'); setMenuOpen(false); }}
            >
              <CreditCard size={20} />
              {t.subscription}
            </div>
            <div 
              className={`menu-item ${currentPage === 'routes' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('routes'); setMenuOpen(false); }}
            >
              <MapPin size={20} />
              {t.routes}
            </div>
            <div 
              className={`menu-item ${currentPage === 'history' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('history'); setMenuOpen(false); }}
            >
              <History size={20} />
              {t.history}
            </div>
            <div
              className={`menu-item ${currentPage === 'profile' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('profile'); setMenuOpen(false); }}
            >
              <Settings size={20} />
              Mon Profil
            </div>
            <div
              className={`menu-item ${currentPage === 'balance' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('balance'); setMenuOpen(false); fetchUserBalance(currentUser?.user_id); }}
            >
              <Wallet size={20} />
              Mon Solde
            </div>
            <div
              className={`menu-item ${currentPage === 'support' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('support'); setMenuOpen(false); }}
            >
              <HelpCircle size={20} />
              Aide & Support
            </div>
            <div
              className={`menu-item ${currentPage === 'inbox' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('inbox'); setMenuOpen(false); }}
              style={{ position: 'relative' }}
            >
              <Inbox size={20} />
              Messages
              {clientUnreadCount > 0 && (
                <span style={{ marginLeft: 'auto', background: '#DC2626', color: 'white', borderRadius: '20px', padding: '2px 7px', fontSize: '0.7rem', fontWeight: '700' }}>{clientUnreadCount}</span>
              )}
            </div>
            <div
              className={`menu-item ${currentPage === 'taxi-order' ? 'active' : ''}`}
              onClick={() => { setCurrentPage('taxi-order'); setMenuOpen(false); }}
            >
              <Car size={20} />
              Commander un Taxi
            </div>
            <div 
              className="menu-item"
              onClick={() => {
                authService.logout();
                setIsAuthenticated(false);
                setCurrentUser(null);
                setCurrentPage('login');
                setMenuOpen(false);
              }}
              style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '1rem', paddingTop: '1rem' }}
            >
              <XCircle size={20} />
              Déconnexion
            </div>
          </nav>
        </div>
      )}

      <div className="app-container">
        {currentPage === 'landing' && (
          <LandingPage
            isAuthenticated={isAuthenticated}
            onLogin={() => setCurrentPage(isAuthenticated ? 'home' : 'login')}
            onRegister={() => setCurrentPage(isAuthenticated ? 'home' : 'register')}
          />
        )}
        {(currentPage === 'login' || currentPage === 'register') && <Stable render={LoginPage} key="LoginPage" />}
        {/* Ancienne inscription par mot de passe : conservée mais plus proposée (connexion par téléphone, 2026-10-30) */}
        {currentPage === 'register-legacy' && <Stable render={RegisterPage} key="RegisterPage" />}
        {currentPage === 'forgot-password' && <Stable render={ForgotPasswordPage} key="ForgotPasswordPage" />}
        {currentPage === 'home' && <Stable render={HomePage} key="HomePage" />}
        {currentPage === 'market' && (
          <MarketApp currentUser={user} isAuthenticated={isAuthenticated} citySlug={user?.city_slug || null} hostPage="market" />
        )}
        {currentPage === 'business' && businessId && (
          <MarketApp currentUser={user} isAuthenticated={isAuthenticated} citySlug={user?.city_slug || null} initialSupplierId={businessId} hostPage="business" />
        )}
        {currentPage === 'qr' && <Stable render={QRCodePage} key="QRCodePage" />}
        {currentPage === 'subscription' && <Stable render={SubscriptionPage} key="SubscriptionPage" />}
        {currentPage === 'routes' && <Stable render={RoutesPage} key="RoutesPage" />}
        {currentPage === 'profile' && <Stable render={ProfilePage} key="ProfilePage" />}
        {currentPage === 'taxi-order' && <TaxiOrderPage />}
        {currentPage === 'balance' && <Stable render={BalancePage} key="BalancePage" />}
        {currentPage === 'inbox' && <Stable render={InboxPage} key="InboxPage" />}
        {currentPage === 'support' && <Stable render={SupportPage} key="SupportPage" />}
        {currentPage === 'history' && currentUser && (
          <div>
            <button onClick={() => setCurrentPage('home')} style={backButtonStyle}>
              <ArrowLeft size={20} />
              Retour
            </button>
            <TripHistoryClient userId={currentUser.user_id} />
          </div>
        )}
      </div>

    </div>
  );
}
